import type {
  BarcodeFormat,
  Brand,
  Category,
  CustomFieldDefinition,
  Item,
  ItemCreate,
  ItemGroupPrice,
  ItemGroupPriceSet,
  ItemType,
  ItemUnitInput,
  ItemVariantInput,
  UnitOfMeasure,
} from '@erp/shared';
import { generateBarcodeValue, isYouTubeUrl, ITEM_COST_PERMISSIONS, MAX_ITEM_BARCODES, type ItemStockSummary } from '@erp/shared';
import {
  ArrowLeft, Barcode, Bookmark, Boxes, Coins, Eye, EyeOff, Factory, FileText, FolderTree, Hash, Image as ImageIcon, Languages,
  type LucideIcon, MonitorPlay, Package, Percent, Plus, Ruler, ScanLine, Shapes,
  SlidersHorizontal, Sparkles, Tag, ToggleRight, Trash2, Type, Users, Video, X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Card, Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Skeleton, Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/shadcn/index.js';
import { CurrencySelect } from '../../components/ui/CurrencySelect.js';
import { Button, Combobox, type ComboboxOption, FormField, RichTextEditor, NumberInput } from '../../components/ui/index.js';
import { DirectionScope, useLayoutDirection } from '../../hooks/useDirection.js';
import { useAnyPermission, usePermission } from '../../hooks/usePermission.js';
import { aiGenerate } from '../../lib/aiContent.js';
import { api } from '../../lib/api.js';
import {
  deleteAttachment,
  fetchAttachmentObjectUrl,
  isImageMime,
  listAttachments,
  uploadAttachment,
} from '../../lib/attachments.js';
import { useAuthStore } from '../../lib/authStore.js';
import { compressImageFile } from '../../lib/imageCompress.js';
import { blockImplicitSubmit } from '../../lib/implicitSubmit.js';
import { moduleEnabled } from '../../lib/modules.js';
import { useEnabledModules } from '../../lib/modulesStore.js';
import { formatMoney, trimMoney } from '../../lib/money.js';
import { normalizeNumericInput, safeDecimal } from '../../lib/numerals.js';
import { getItemGroupPrices, listPriceLists, setItemGroupPrices } from '../../lib/pricing.js';
import { TranslationEditor } from '../cms/TranslationEditor.js';
import { ImageEditorDialog } from '../common/ImageEditorDialog.js';

import { AiImageDialog } from './AiImageDialog.js';
import { BarcodePreview } from './BarcodePreview.js';
import { comboRowsToPayload, type ComboDraftRow, ItemComboTab } from './ItemComboTab.js';
import {
  AI_TRANSLATE_MAX,
  flattenCategoryOptions,
  type OptionGroupDraft,
  optionGroupsFromVariants,
  splitOptionValues,
} from './itemHelpers.js';
import { ItemRecipeTab } from './ItemRecipeTab.js';
import { EMPTY_OPENING, hasOpeningStock, ItemStockTab, type OpeningStock } from './ItemStockTab.js';

const ITEM_TYPES: ItemType[] = ['product', 'service'];
const BARCODE_FORMATS: BarcodeFormat[] = ['code128', 'ean13', 'ean8', 'upc_a', 'qr'];

interface NumberingValue {
  barcode?: { defaultFormat?: BarcodeFormat };
}
interface SettingsListItem {
  key: string;
  value: unknown;
}

interface GalleryImage {
  key: string;
  id?: string;
  url: string;
  blob?: Blob;
}

/**
 * One customer group (price list) with this item's directly-entered prices in it,
 * one per selling unit. `base` is the base-unit (1 piece) price; `units` is a
 * price string per packaging unit, parallel by index to `form.units`. A single
 * `currencyCode` applies to every unit in this group (the group's own currency —
 * never converted). Blank clears that (list, unit).
 */
interface GroupPriceRow {
  priceListId: string;
  priceListName: string;
  currencyCode: string;
  base: string;
  units: string[];
}

function blankForm(baseCurrency: string, barcodeFormat: BarcodeFormat): ItemCreate {
  return {
    code: '',
    name: '',
    itemType: 'product',
    isSellable: true,
    isPurchasable: true,
    // A product counts its stock unless someone unticks it — an untracked product
    // is never taken off the shelf by a sale.
    isInventoryTracked: true,
    isRawMaterial: false,
    earnsCashback: true,
    isHidden: false,
    isFeatured: false,
    // Ticked by default: a product nobody publishes is invisible to the shop
    // and the app, and "I added it but it isn't there" was the usual result of
    // making the operator go to /cms/products afterwards to find that out.
    isPublished: true,
    unitOfMeasure: 'ea',
    saleCurrencyCode: baseCurrency,
    barcodeFormat,
    customFields: {},
    units: [],
    variants: [],
    barcodes: [],
    extraCategoryIds: [],
  };
}

/**
 * The API's decimals arrive as 4dp strings — "7500.0000", factor "12.0000" —
 * and pouring those into inputs makes every price read like a fraction the
 * shop never typed. Trim the noise once at seed time; the values round-trip
 * identically (the server parses decimals, "7500" === "7500.0000").
 */
const tidy = (v: string | null | undefined): string | undefined =>
  v == null ? undefined : trimMoney(v);

function formFromItem(it: Item, baseCurrency: string): ItemCreate {
  return {
    code: it.code,
    name: it.name,
    description: it.description ?? undefined,
    itemType: it.itemType,
    isSellable: it.isSellable,
    isPurchasable: it.isPurchasable,
    isInventoryTracked: it.isInventoryTracked,
    isRawMaterial: it.isRawMaterial,
    earnsCashback: it.earnsCashback,
    unitOfMeasure: it.unitOfMeasure,
    salePrice: tidy(it.salePrice),
    saleCurrencyCode: it.saleCurrencyCode ?? baseCurrency,
    // purchasePrice / purchaseCurrencyCode are intentionally NOT loaded: the form
    // no longer edits them, and leaving them out of the form state guarantees the
    // save can never overwrite what a purchase document already recorded.
    taxRate: tidy(it.taxRate),
    barcode: it.barcode ?? undefined,
    barcodeFormat: it.barcodeFormat,
    barcodes: [...(it.barcodes ?? [])],
    categoryId: it.categoryId ?? undefined,
    extraCategoryIds: [...(it.extraCategoryIds ?? [])],
    brandId: it.brandId ?? undefined,
    customFields: it.customFields ?? {},
    videoUrl: it.videoUrl ?? undefined,
    isHidden: it.isHidden,
    isFeatured: it.isFeatured,
    isPublished: it.isPublished,
    units: it.units.map((u) => ({
      name: u.name,
      factor: trimMoney(u.factor),
      salePrice: tidy(u.salePrice),
      // Carried through (but not editable) — units are deleted + recreated on save,
      // so dropping this would erase any purchase price an older item still has.
      purchasePrice: tidy(u.purchasePrice),
      barcode: u.barcode ?? undefined,
      barcodes: [...(u.barcodes ?? [])],
      isActive: u.isActive,
    })),
    variants: it.variants.map((v) => ({
      name: v.name,
      nameAr: v.nameAr ?? undefined,
      nameEn: v.nameEn ?? undefined,
      barcode: v.barcode ?? undefined,
      salePrice: tidy(v.salePrice),
      stockQty: tidy(v.stockQty),
    })),
  };
}

/**
 * The add/edit product form stays left-to-right in Arabic and Kurdish (the rest
 * of the admin follows the language): SKUs, barcodes, prices and English names
 * are entered here. The scope also makes its dialogs, tabs and dropdowns LTR.
 */
/** What the shop's costs are kept in — it buys in dollars (the owner's rule, 2026-10-25). */
const COST_CURRENCY = 'USD';

export function ItemFormPage(props: { edit?: boolean }): JSX.Element {
  return (
    <DirectionScope dir="ltr">
      <div dir="ltr">
        <ItemFormPageBody {...props} />
      </div>
    </DirectionScope>
  );
}

function ItemFormPageBody({ edit = false }: { edit?: boolean }): JSX.Element {
  const { t, i18n } = useTranslation('items');
  const { t: tc } = useTranslation();
  const { t: ti } = useTranslation('inventory');
  const dir = useLayoutDirection();
  const { t: tCombos } = useTranslation('combos');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const tenant = useAuthStore((s) => s.tenant);
  const baseCurrency = tenant?.baseCurrencyCode ?? 'USD';
  const canUploadImg = usePermission('attachments.write');
  const canReadStock = usePermission('stock.read');
  /** Who may see what the shop pays — the same rights the item profile's cost needs. */
  const canCost = useAnyPermission(ITEM_COST_PERMISSIONS);
  /** Who may move the stock's cost basis (the warehouses' average cost). */
  const canSetCost = usePermission('stock.adjust');
  /**
   * «سعر الشراء» — what one piece costs the shop. Kept apart from `form` so a save
   * writes it only when somebody changed it: a save that did not touch it must
   * never overwrite a cost a purchase put there.
   */
  const [purchase, setPurchase] = useState<{ price: string; entry: string; dirty: boolean }>({ price: '', entry: COST_CURRENCY, dirty: false });
  /** Today's rates FROM the cost currency (1 USD = rate × code) — to turn a price typed in dinars into dollars. */
  const [costRates, setCostRates] = useState<Record<string, number>>({});
  useEffect(() => {
    let on = true;
    void api()
      .get<{ items: Array<{ code: string; rate: string }> }>('/currencies/display-rates', { params: { base: COST_CURRENCY } })
      .then((r) => { if (on) setCostRates(Object.fromEntries(r.data.items.map((x) => [x.code, Number(x.rate)]))); })
      .catch(() => undefined);
    return () => { on = false; };
  }, []);
  /** An amount in `ccy` as the cost currency, at today's rate ('' when there is no rate to use). */
  const toCostCurrency = (amount: string, ccy: string): string => {
    if (ccy === COST_CURRENCY) return amount;
    const rate = costRates[ccy];
    return rate && rate > 0 ? safeDecimal(amount).dividedBy(rate).toDecimalPlaces(4).toString() : '';
  };
  /** Each warehouse's current average cost (edit only) — what a new purchase price replaces. */
  const [stockCosts, setStockCosts] = useState<Array<{ name: string; avgCost: string; currency: string }>>([]);

  const [categories, setCategories] = useState<Category[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [uoms, setUoms] = useState<UnitOfMeasure[]>([]);
  // Which unit picker is quick-adding a unit: 'base', a pack-unit index, or null.
  const [addUnitFor, setAddUnitFor] = useState<'base' | number | null>(null);
  const [newUnitName, setNewUnitName] = useState('');
  const [savingUnit, setSavingUnit] = useState(false);
  const [unitError, setUnitError] = useState<string | null>(null);
  const [customFieldDefs, setCustomFieldDefs] = useState<CustomFieldDefinition[]>([]);
  const [initial, setInitial] = useState<Item | null>(null);
  const [form, setForm] = useState<ItemCreate | null>(null);
  // Per-customer-group sale prices (one row per active price list), each entered
  // directly in its own currency — never converted.
  const [groupRows, setGroupRows] = useState<GroupPriceRow[]>([]);
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [generatingSku, setGeneratingSku] = useState(false);
  // Deep-linkable (?tab=recipe from the BOMs page); anything unknown falls to
  // the Tabs component ignoring it, which lands on the default styling-wise.
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('tab') ?? 'general');
  const [variantsOpen, setVariantsOpen] = useState(false);
  // Variants are a toggleable module — hide the editor entirely when it's off.
  const variantsTenantId = useAuthStore((s) => s.tenant?.id);
  const enabledModulesMap = useEnabledModules(variantsTenantId);
  const variantsEnabled = moduleEnabled(enabledModulesMap, 'variants');
  const aiEnabled = moduleEnabled(enabledModulesMap, 'ai');
  const imagegenEnabled = moduleEnabled(enabledModulesMap, 'imagegen');
  const taxEnabled = moduleEnabled(enabledModulesMap, 'tax');
  // Customer-group pricing is a toggleable module; only offer the section when on.
  const pricingEnabled = moduleEnabled(enabledModulesMap, 'pricing');
  // Extra category memberships — the multi_category module's editor.
  const multiCatEnabled = moduleEnabled(enabledModulesMap, 'multi_category');
  const manufacturingEnabled = moduleEnabled(enabledModulesMap, 'manufacturing');
  const canReadMfg = usePermission('manufacturing.read');
  const canWriteMfg = usePermission('manufacturing.write');
  // Bundles ride on the item's own permissions: whoever may edit the product
  // may say what is in its box.
  const combosEnabled = moduleEnabled(enabledModulesMap, 'combos');
  const canWriteItems = usePermission('items.write');
  /** The bundle's contents typed on the CREATE form. Held HERE, not in the tab:
   *  switching tabs unmounts the panel, and rows kept inside it would vanish. */
  const [comboDraft, setComboDraft] = useState<ComboDraftRow[]>([]);
  const [aiBusy, setAiBusy] = useState<null | 'description'>(null);
  const [aiImgOpen, setAiImgOpen] = useState(false);
  // Client-side "options" builder (e.g. Color: Red,Blue) that generates the variant
  // rows. Each option carries an English AND an Arabic name + comma-separated values
  // (parallel by position) so the generated variants are bilingual.
  const [optionGroups, setOptionGroups] = useState<OptionGroupDraft[]>([]);
  /** Which option group is mid-translate, and whether the name sweep is running. */
  const [aiGroupBusy, setAiGroupBusy] = useState<number | null>(null);
  const [aiNamesBusy, setAiNamesBusy] = useState(false);
  const [aiNamesEnBusy, setAiNamesEnBusy] = useState(false);
  // Translations typed on the CREATE form (locale → field → value). The item has no
  // id yet, so they're held here and saved right after the item is created.
  const [pendingTranslations, setPendingTranslations] = useState<Record<string, Record<string, string>>>({});
  // Opening stock typed on the CREATE form — same deal: posted as an adjustment
  // once the item exists. On an EXISTING item the Stock tab writes directly.
  const [opening, setOpening] = useState<OpeningStock>(EMPTY_OPENING);
  /**
   * The purchase price as the API takes it — ALWAYS in dollars ('' = none). The
   * shop buys in USD (the owner's rule, 2026-10-25); a price typed in dinars is
   * converted at today's rate, never stored as dinars.
   */
  const purchaseTyped = (() => {
    const v = normalizeNumericInput(purchase.price).trim();
    return v !== '' && safeDecimal(v).gte(0) ? safeDecimal(v).toDecimalPlaces(4).toString() : '';
  })();
  const purchaseValue = purchaseTyped ? toCostCurrency(purchaseTyped, purchase.entry) : '';
  const purchaseCurrency = COST_CURRENCY;
  useEffect(() => {
    if (!edit || !id || !canCost) return undefined;
    let on = true;
    void api()
      .get<ItemStockSummary>(`/stock/items/${id}/summary`)
      .then((r) => {
        if (!on) return;
        setStockCosts(r.data.warehouses
          .filter((w) => w.avgCost && w.avgCostCurrencyCode && Number(w.avgCost) > 0)
          .map((w) => ({ name: w.name, avgCost: w.avgCost!, currency: w.avgCostCurrencyCode! })));
      })
      .catch(() => { /* no stock rights — the field still works */ });
    return () => { on = false; };
  }, [edit, id, canCost]);

  // Image gallery (oldest-first; the first image is the list thumbnail).
  const imgInputRef = useRef<HTMLInputElement>(null);
  const urlsRef = useRef<string[]>([]);
  const keySeq = useRef(0);
  const [images, setImages] = useState<GalleryImage[]>([]);
  const [removedIds, setRemovedIds] = useState<string[]>([]);
  const [editorFile, setEditorFile] = useState<File | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const track = (u: string): string => {
    urlsRef.current.push(u);
    return u;
  };

  useEffect(() => {
    let active = true;
    void (async () => {
      const [catRes, brandRes, uomRes, cfRes, setRes] = await Promise.allSettled([
        api().get<{ items: Category[] }>('/categories'),
        api().get<{ items: Brand[] }>('/brands'),
        api().get<{ items: UnitOfMeasure[] }>('/units-of-measure'),
        api().get<{ items: CustomFieldDefinition[] }>('/custom-fields'),
        api().get<{ items: SettingsListItem[] }>('/settings'),
      ]);
      if (!active) return;
      if (catRes.status === 'fulfilled') setCategories(catRes.value.data.items);
      if (brandRes.status === 'fulfilled') setBrands(brandRes.value.data.items);
      if (uomRes.status === 'fulfilled') setUoms(uomRes.value.data.items);
      if (cfRes.status === 'fulfilled') {
        setCustomFieldDefs(cfRes.value.data.items.filter((d) => d.isActive));
      }
      let fmt: BarcodeFormat = 'code128';
      if (setRes.status === 'fulfilled') {
        const numbering = setRes.value.data.items.find((s) => s.key === 'items.numbering')?.value as
          | NumberingValue
          | undefined;
        if (numbering?.barcode?.defaultFormat) fmt = numbering.barcode.defaultFormat;
      }

      if (edit && id) {
        try {
          // ?hiddenUnits=1: this form lists hidden units too, so they can be
          // switched back on — and so saving never drops them.
          const r = await api().get<Item>(`/items/${id}?hiddenUnits=1`);
          if (!active) return;
          setInitial(r.data);
          setForm(formFromItem(r.data, baseCurrency));
          setPurchase({
            price: r.data.purchasePrice ? String(Number(r.data.purchasePrice)) : '',
            // Shown in the currency it is stored in; saved back in dollars.
            entry: r.data.purchaseCurrencyCode ?? COST_CURRENCY,
            dirty: false,
          });
          // Show the options this product's variants were built from, so editing
          // starts from what is there instead of a blank builder.
          setOptionGroups(optionGroupsFromVariants(r.data.variants) ?? []);
        } catch {
          if (active) setError(tc('errors:server'));
        }
        if (canUploadImg && id) {
          try {
            const list = (await listAttachments('item', id))
              .filter((a) => isImageMime(a.mimeType))
              .sort((a, b) => a.uploadedAt.localeCompare(b.uploadedAt));
            const loaded: GalleryImage[] = [];
            for (const a of list) {
              const u = track(await fetchAttachmentObjectUrl(a.id));
              loaded.push({ key: `e${a.id}`, id: a.id, url: u });
            }
            if (active) setImages(loaded);
          } catch {
            /* images are non-blocking */
          }
        }
      } else {
        setForm(blankForm(baseCurrency, fmt));
      }
      if (active) setReady(true);
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [edit, id]);

  useEffect(
    () => () => {
      urlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    },
    [],
  );

  // Seed the per-group price rows: on edit, the item's current group prices per
  // unit; on create, one blank row per active price list (in each list's own
  // currency). Each row carries a base-unit price + one price per packaging unit
  // (parallel by index to `form.units`, in sort_order — the same order the API
  // enumerates the unit axis, base first).
  useEffect(() => {
    if (!pricingEnabled) return;
    let active = true;
    void (async () => {
      try {
        if (edit && id) {
          const rows = await getItemGroupPrices(id);
          if (!active) return;
          // Group the (list × unit) entries back into one row per list.
          const byList = new Map<string, ItemGroupPrice[]>();
          for (const r of rows) {
            const list = byList.get(r.priceListId) ?? [];
            list.push(r);
            byList.set(r.priceListId, list);
          }
          const built: GroupPriceRow[] = [];
          for (const [priceListId, entries] of byList) {
            const baseEntry = entries.find((e) => e.itemUnitId == null);
            const unitEntries = entries.filter((e) => e.itemUnitId != null);
            const currency =
              baseEntry?.currencyCode ??
              unitEntries.find((e) => e.currencyCode)?.currencyCode ??
              entries[0]?.listCurrencyCode ??
              baseCurrency;
            built.push({
              priceListId,
              priceListName: entries[0]?.priceListName ?? '',
              currencyCode: currency,
              base: baseEntry?.unitPrice ? trimMoney(baseEntry.unitPrice) : '',
              units: unitEntries.map((e) => (e.unitPrice ? trimMoney(e.unitPrice) : '')),
            });
          }
          setGroupRows(built);
        } else {
          const lists = await listPriceLists();
          if (!active) return;
          setGroupRows(
            lists
              .filter((l) => l.isActive)
              .map((l) => ({
                priceListId: l.id,
                priceListName: l.name,
                currencyCode: l.currencyCode ?? baseCurrency,
                base: '',
                units: [],
              })),
          );
        }
      } catch {
        /* group prices are non-blocking — the item still saves */
      }
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingEnabled, edit, id]);

  const onPickFiles = async (files: FileList): Promise<void> => {
    const arr = Array.from(files);
    if (arr.length === 0) return;
    // A single pick goes through the crop/rotate editor; multiple are bulk-
    // compressed and appended (the first stays the thumbnail).
    if (arr.length === 1 && arr[0]) {
      setEditorFile(arr[0]);
      setEditorOpen(true);
      return;
    }
    for (const f of arr) {
      try {
        const blob = await compressImageFile(f);
        keySeq.current += 1;
        setImages((imgs) => [
          ...imgs,
          { key: `n${keySeq.current}`, url: track(URL.createObjectURL(blob)), blob },
        ]);
      } catch {
        /* skip a file that fails to compress */
      }
    }
  };

  const onEditedImage = (blob: Blob): void => {
    keySeq.current += 1;
    setImages((imgs) => [
      ...imgs,
      { key: `n${keySeq.current}`, url: track(URL.createObjectURL(blob)), blob },
    ]);
    setEditorOpen(false);
    setEditorFile(null);
  };
  const onRemoveImage = (img: GalleryImage): void => {
    if (img.blob) URL.revokeObjectURL(img.url);
    if (img.id) setRemovedIds((r) => [...r, img.id!]);
    setImages((imgs) => imgs.filter((g) => g.key !== img.key));
  };

  const onAutoSku = async (): Promise<void> => {
    setError(null);
    setGeneratingSku(true);
    try {
      const r = await api().post<{ sku: string }>('/items/sku/next', {});
      setForm((f) => (f ? { ...f, code: r.data.sku } : f));
    } catch {
      setError(tc('errors:server'));
    } finally {
      setGeneratingSku(false);
    }
  };

  const setField = (patch: Partial<ItemCreate>): void => setForm((f) => (f ? { ...f, ...patch } : f));

  // Quick-create a unit of measure without leaving the item form, then select it.
  const addQuickUnit = async (): Promise<void> => {
    const name = newUnitName.trim();
    if (!name) return;
    setSavingUnit(true);
    setUnitError(null);
    try {
      await api().post('/units-of-measure', { name, sort: uoms.length });
      const r = await api().get<{ items: UnitOfMeasure[] }>('/units-of-measure');
      setUoms(r.data.items);
      if (addUnitFor === 'base') setField({ unitOfMeasure: name });
      else if (typeof addUnitFor === 'number') setUnit(addUnitFor, { name });
      setNewUnitName('');
      setAddUnitFor(null);
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } }).response?.data?.code;
      setUnitError(
        code === 'conflict'
          ? t('create.unitDuplicate', { defaultValue: 'That unit name already exists.' })
          : t('create.unitError', { defaultValue: 'Could not add the unit.' }),
      );
    } finally {
      setSavingUnit(false);
    }
  };
  // The "＋ new unit" button + its inline input, reused by the base + pack pickers.
  const addUnitButton = (target: 'base' | number): JSX.Element => (
    <Button
      type="button"
      variant="secondary"
      className="flex-none"
      onClick={() => { setAddUnitFor((cur) => (cur === target ? null : target)); setNewUnitName(''); setUnitError(null); }}
      title={t('create.addUnit', { defaultValue: 'New unit' })}
      aria-label={t('create.addUnit', { defaultValue: 'New unit' })}
    >
      <Plus className="h-4 w-4" aria-hidden />
    </Button>
  );
  const addUnitForm = (target: 'base' | number): JSX.Element | null =>
    addUnitFor === target ? (
      <div className="mt-2 flex flex-col gap-1">
        <div className="flex gap-2">
          <Input
            autoFocus
            value={newUnitName}
            onChange={(e) => setNewUnitName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void addQuickUnit(); } }}
            placeholder={t('create.addUnitPlaceholder', { defaultValue: 'e.g. Box, Dozen, Litre' })}
            maxLength={40}
            className="flex-1"
          />
          <Button type="button" onClick={() => void addQuickUnit()} loading={savingUnit} disabled={!newUnitName.trim()}>
            {tc('actions.add', { defaultValue: 'Add' })}
          </Button>
        </div>
        {unitError && <p className="text-xs text-danger">{unitError}</p>}
      </div>
    ) : null;
  const setCustom = (code: string, value: unknown): void =>
    setForm((f) => {
      if (!f) return f;
      const next: Record<string, unknown> = { ...(f.customFields ?? {}) };
      if (value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
        delete next[code];
      } else {
        next[code] = value;
      }
      return { ...f, customFields: next };
    });
  const setUnit = (i: number, patch: Partial<ItemUnitInput>): void =>
    setForm((f) =>
      f ? { ...f, units: (f.units ?? []).map((u, idx) => (idx === i ? { ...u, ...patch } : u)) } : f,
    );
  // A factor always counts BASE units, so an outer level reads as a big number:
  // a carton of 18 packs of 24 pieces is 432, not 18. Say how it breaks down
  // against the level below, which is how the stockroom actually counts it.
  const unitBreakdown = (i: number): string | null => {
    const units = form?.units ?? [];
    const f = Number(units[i]?.factor);
    const below = units[i - 1];
    const bf = Number(below?.factor);
    if (!Number.isFinite(f) || f <= 0 || !below?.name || !Number.isFinite(bf) || bf <= 0) return null;
    const n = f / bf;
    if (!Number.isInteger(n) || n <= 1) return null;
    return `= ${n} × ${below.name}`;
  };
  // Add/remove a packaging unit — and keep every group-price row's `units` array
  // aligned by index (a new unit gets a blank price in each group; removing unit i
  // drops index i everywhere) so the per-unit group prices track `form.units`.
  /** Split "111 222,333" into codes and hang them on unit `i`, ignoring repeats. */
  const addUnitCodes = (i: number, text: string): void => {
    const incoming = text.split(/[;,\s]+/).map((c) => c.trim()).filter(Boolean);
    if (incoming.length === 0) return;
    setForm((f) => {
      if (!f) return f;
      const units = [...(f.units ?? [])];
      const u = units[i];
      if (!u) return f;
      const seen = new Set(u.barcodes ?? []);
      const next = [...(u.barcodes ?? [])];
      for (const c of incoming) {
        if (seen.has(c) || next.length >= MAX_ITEM_BARCODES) continue;
        seen.add(c);
        next.push(c);
      }
      units[i] = { ...u, barcodes: next };
      return { ...f, units };
    });
  };

  // Show / hide one unit everywhere it could be sold. Takes effect on Save,
  // like every other field here.
  const toggleUnitShown = (i: number): void => {
    const u = form?.units?.[i];
    if (!u) return;
    setUnit(i, { isActive: u.isActive === false });
  };
  const hiddenMark = (u: ItemUnitInput): string =>
    u.isActive === false ? ` (${t('form.units.hiddenShort', { defaultValue: 'hidden' })})` : '';

  const addUnit = (): void => {
    if (!form) return;
    setForm((f) => (f ? { ...f, units: [...(f.units ?? []), { name: '', factor: '' }] } : f));
    setGroupRows((rows) => rows.map((r) => ({ ...r, units: [...r.units, ''] })));
  };
  const removeUnit = (i: number): void => {
    setForm((f) => (f ? { ...f, units: (f.units ?? []).filter((_, idx) => idx !== i) } : f));
    setGroupRows((rows) => rows.map((r) => ({ ...r, units: r.units.filter((_, idx) => idx !== i) })));
  };

  // Patch one customer-group row: its base-unit price, or a per-packaging-unit
  // price (by unit index, parallel to form.units). The currency is FIXED to the
  // price list's own currency — not editable on the item form.
  const setGroupBase = (priceListId: string, base: string): void =>
    setGroupRows((rows) => rows.map((r) => (r.priceListId === priceListId ? { ...r, base } : r)));
  const setGroupUnit = (priceListId: string, unitIndex: number, value: string): void =>
    setGroupRows((rows) =>
      rows.map((r) => {
        if (r.priceListId !== priceListId) return r;
        // Pad so a per-unit price is stored even if this row's units array lagged
        // (e.g. a packaging unit was added while the price-list seed was still
        // in flight on create — otherwise .map wouldn't reach unitIndex).
        const units = [...r.units];
        while (units.length <= unitIndex) units.push('');
        units[unitIndex] = value;
        return { ...r, units };
      }),
    );

  // ---- Extra barcodes (the same product under a second manufacturer label) ----
  const setExtraBarcode = (i: number, value: string): void =>
    setForm((f) => (f ? { ...f, barcodes: (f.barcodes ?? []).map((b, idx) => (idx === i ? value : b)) } : f));
  const addExtraBarcode = (): void =>
    setForm((f) => (f && (f.barcodes ?? []).length < MAX_ITEM_BARCODES ? { ...f, barcodes: [...(f.barcodes ?? []), ''] } : f));
  const removeExtraBarcode = (i: number): void =>
    setForm((f) => (f ? { ...f, barcodes: (f.barcodes ?? []).filter((_, idx) => idx !== i) } : f));
  /**
   * Paste several codes at once. Splits on whitespace/comma/semicolon — the ways
   * someone actually has them written down ("111111 222222", a column copied out
   * of Excel) — and appends them as separate rows, skipping ones already listed.
   */
  const appendPastedBarcodes = (text: string): void => {
    const parts = text.split(/[;,\s]+/).map((s) => s.trim()).filter(Boolean);
    if (parts.length === 0) return;
    setForm((f) => {
      if (!f) return f;
      const existing = (f.barcodes ?? []).filter((b) => b.trim());
      const seen = new Set(existing);
      for (const p of parts) {
        if (seen.has(p) || existing.length >= MAX_ITEM_BARCODES) continue;
        seen.add(p);
        existing.push(p);
      }
      return { ...f, barcodes: existing };
    });
  };

  // ---- Variants (product options) ----
  const setVariant = (i: number, patch: Partial<ItemVariantInput>): void =>
    setForm((f) =>
      f ? { ...f, variants: (f.variants ?? []).map((v, idx) => (idx === i ? { ...v, ...patch } : v)) } : f,
    );
  const addVariant = (): void =>
    setForm((f) => (f && (f.variants ?? []).length < 100 ? { ...f, variants: [...(f.variants ?? []), { name: '' }] } : f));
  const removeVariant = (i: number): void =>
    setForm((f) => (f ? { ...f, variants: (f.variants ?? []).filter((_, idx) => idx !== i) } : f));
  // Cartesian product of the option groups → variant rows, preserving the
  // price/barcode/stock of any existing row whose name still applies. English and
  // Arabic combinations are built in lockstep (each Arabic value is paired to its
  // English value by position; a missing Arabic value falls back to the English one)
  // so every generated variant gets both a `name` and a `nameAr`.
  const generateVariants = (): void => {
    const groups = optionGroups
      .map((g) => ({
        values: splitOptionValues(g.values),
        // Holes kept: the Arabic values pair to the English ones by position.
        valuesAr: splitOptionValues(g.valuesAr, true),
      }))
      .filter((g) => g.values.length > 0);
    if (groups.length === 0) return;
    let combos: Array<{ en: string; ar: string }> = [{ en: '', ar: '' }];
    for (const g of groups) {
      const next: Array<{ en: string; ar: string }> = [];
      for (const c of combos) {
        g.values.forEach((v, vi) => {
          const vAr = g.valuesAr[vi]?.trim() || v;
          next.push({
            en: c.en ? `${c.en} / ${v}` : v,
            ar: c.ar ? `${c.ar} / ${vAr}` : vAr,
          });
        });
      }
      combos = next;
    }
    setForm((f) => {
      if (!f) return f;
      const existing = new Map((f.variants ?? []).map((v) => [v.name, v]));
      return {
        ...f,
        variants: combos.map((c) => {
          const prev = existing.get(c.en);
          return prev ? { ...prev, nameAr: prev.nameAr || c.ar } : { name: c.en, nameAr: c.ar };
        }),
      };
    });
  };
  const setOptionGroup = (i: number, patch: Partial<OptionGroupDraft>): void =>
    setOptionGroups((gs) => gs.map((g, idx) => (idx === i ? { ...g, ...patch } : g)));
  /**
   * An empty variant price means "charge the product's price" — the API stores
   * null and every reader falls back to the base. That rule was invisible: the
   * field just looked unfilled, so people typed the base price into all of them
   * and any later price change left the variants behind. It is a checkbox now,
   * on by default, and unticking seeds the box with the base price to edit from.
   */
  const variantUsesBasePrice = (v: ItemVariantInput): boolean => !(v.salePrice ?? '').trim();
  const setVariantPriceMode = (i: number, useBase: boolean): void =>
    setVariant(i, { salePrice: useBase ? undefined : (form?.salePrice ?? '').trim() || '0' });

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!form) return;
    setError(null);
    // Required fields live on the General tab; if they're blank, jump there so
    // the user sees them (the form is noValidate + tabs unmount inactive panels).
    if (!form.code.trim() || !form.name.trim()) {
      setTab('general');
      setError(t('errors.required', { defaultValue: 'Code and name are required.' }));
      return;
    }
    // An opening quantity with no warehouse would be silently dropped — say so on
    // the tab that owns it rather than saving the item without the stock.
    const openQty = normalizeNumericInput(opening.quantity).trim();
    if (!edit && openQty !== '' && safeDecimal(openQty).gt(0) && !opening.warehouseId) {
      setTab('stock');
      setError(ti('itemStock.openingNeedsWarehouse'));
      return;
    }
    // A half-finished packaging unit used to be dropped on the floor right here:
    // the filter below wants BOTH a name and a factor, so a row with only one of
    // them was quietly discarded, the item saved, the toast said "saved" and the
    // unit was simply gone. Say what is missing, on the tab that holds it.
    //
    // The pattern is the API's own (ItemUnitInputSchema.factor) on purpose — a
    // factor this accepts and the server rejects would be the same silent
    // failure wearing a different hat.
    const badUnit = (form.units ?? []).findIndex((u) => {
      const name = u.name.trim();
      const factor = u.factor.trim();
      if (!name && !factor) return false; // an untouched blank row: drop it quietly
      return !name || !/^\d+(\.\d{1,4})?$/.test(factor) || Number(factor) <= 0;
    });
    if (badUnit >= 0) {
      setTab('pricing');
      setError(t('form.units.incomplete', {
        n: badUnit + 1,
        defaultValue: 'Unit row {{n}} needs both a unit and a factor greater than zero.',
      }));
      return;
    }
    setSaving(true);
    // Every unit, hidden ones included, each saying whether it is on sale —
    // and `includesHiddenUnits` below tells the API this list is complete, so a
    // unit removed here is removed and a hidden one stays hidden.
    const cleanUnits = (form.units ?? [])
      .filter((u) => u.name.trim() && u.factor.trim())
      .map((u) => ({ ...u, isActive: u.isActive !== false }));
    const cleanVariants = (form.variants ?? [])
      .filter((v) => v.name.trim())
      .map((v) => ({
        name: v.name.trim(),
        ...(v.nameAr?.trim() ? { nameAr: v.nameAr.trim() } : {}),
        ...(v.nameEn?.trim() ? { nameEn: v.nameEn.trim() } : {}),
        ...(v.barcode?.trim() ? { barcode: v.barcode.trim() } : {}),
        ...(v.salePrice?.trim() ? { salePrice: v.salePrice.trim() } : {}),
        ...(v.stockQty?.trim() ? { stockQty: v.stockQty.trim() } : {}),
      }));
    // Always sent (even empty) so clearing the last extra barcode actually removes
    // it — the API treats an omitted `barcodes` as "leave untouched".
    const cleanBarcodes = (form.barcodes ?? []).map((b) => b.trim()).filter(Boolean);
    let itemId: string | undefined;
    // The saved item (with its reconciled units + FRESH ids) — the only source of
    // the current unit ids, since item_units are hard-deleted+recreated on save.
    let savedItem: Item | undefined;
    try {
      if (edit && initial) {
        const updated = await api().patch<Item>(`/items/${initial.id}`, {
          code: form.code.trim(),
          name: form.name.trim(),
          description: form.description?.trim() || null,
          itemType: form.itemType,
          isSellable: form.isSellable,
          earnsCashback: form.earnsCashback,
          isPurchasable: form.isPurchasable,
          isInventoryTracked: form.isInventoryTracked,
          unitOfMeasure: form.unitOfMeasure,
          salePrice: form.salePrice && form.salePrice.trim() ? form.salePrice : null,
          saleCurrencyCode: form.salePrice && form.salePrice.trim() ? form.saleCurrencyCode : null,
          // The purchase price only when somebody changed it: the API writes only
          // keys that are present, so an untouched one stays exactly as it was.
          ...(canCost && purchase.dirty ? {
            purchasePrice: purchaseValue,
            purchaseCurrencyCode: purchaseValue ? purchaseCurrency : null,
          } : {}),
          taxRate: form.taxRate && form.taxRate.trim() ? form.taxRate : null,
          barcode: form.barcode && form.barcode.trim() ? form.barcode.trim() : null,
          barcodeFormat: form.barcodeFormat ?? 'code128',
          categoryId: form.categoryId ?? null,
          extraCategoryIds: form.extraCategoryIds ?? [],
          brandId: form.brandId ?? null,
          customFields: form.customFields ?? {},
          videoUrl: form.videoUrl && form.videoUrl.trim() ? form.videoUrl.trim() : null,
          isHidden: form.isHidden ?? false,
          isFeatured: form.isFeatured ?? false,
          isPublished: form.isPublished ?? false,
          units: cleanUnits,
          includesHiddenUnits: true,
          variants: cleanVariants,
          barcodes: cleanBarcodes,
        });
        itemId = initial.id;
        savedItem = updated.data;
      } else {
        const payload: ItemCreate = { ...form, code: form.code.trim(), name: form.name.trim() };
        if (!payload.description?.trim()) delete payload.description;
        if (!payload.salePrice) {
          delete payload.salePrice;
          delete payload.saleCurrencyCode;
        }
        // A purchase price typed on the new item is its cost from day one; left
        // empty, the first purchase document sets it.
        if (canCost && purchaseValue) {
          payload.purchasePrice = purchaseValue;
          payload.purchaseCurrencyCode = purchaseCurrency;
        } else {
          delete payload.purchasePrice;
          delete payload.purchaseCurrencyCode;
        }
        if (!payload.barcode || !payload.barcode.trim()) delete payload.barcode;
        else payload.barcode = payload.barcode.trim();
        if (!payload.videoUrl || !payload.videoUrl.trim()) delete payload.videoUrl;
        else payload.videoUrl = payload.videoUrl.trim();
        payload.units = cleanUnits;
        payload.includesHiddenUnits = true;
        payload.variants = cleanVariants;
        payload.barcodes = cleanBarcodes;
        const created = await api().post<Item>('/items', payload);
        itemId = created.data.id;
        savedItem = created.data;
      }
      if (itemId && canUploadImg) {
        try {
          for (const rid of removedIds) await deleteAttachment(rid);
          for (const g of images) {
            if (g.blob) {
              await uploadAttachment(
                'item',
                itemId,
                new File([g.blob], 'item-image.jpg', { type: 'image/jpeg' }),
              );
            }
          }
        } catch {
          /* images are non-blocking — manage later via the Files dialog */
        }
      }
      // Save the per-customer-group prices once we have the item id. Non-fatal:
      // the item is already saved. Prices are stored as entered (never converted);
      // a blank price clears that (group, unit).
      if (itemId && pricingEnabled && groupRows.length > 0) {
        try {
          // Map each form unit slot → its FRESH saved unit id. reconcileUnits hard-
          // deletes + reinserts units on every save, so their ids change; the save
          // response is the only source of the current ids. cleanUnits (units with
          // name+factor) preserves order and maps 1:1 to savedItem.units by index,
          // so we walk form.units, advancing through savedItem.units for each unit
          // that survived the clean filter.
          const savedUnits = savedItem?.units ?? [];
          const formIndexToUnitId = new Map<number, string>();
          let k = 0;
          (form.units ?? []).forEach((u, i) => {
            if (u.name.trim() && u.factor.trim()) {
              const su = savedUnits[k];
              if (su) formIndexToUnitId.set(i, su.id);
              k += 1;
            }
          });
          const prices: ItemGroupPriceSet['prices'] = [];
          for (const row of groupRows) {
            const currencyCode = row.currencyCode || null;
            // Base unit (itemUnitId null).
            prices.push({
              priceListId: row.priceListId,
              itemUnitId: null,
              unitPrice: row.base.trim() ? row.base.trim() : null,
              currencyCode,
            });
            // Packaging units — only those that survived to a fresh saved unit id.
            (form.units ?? []).forEach((_, i) => {
              const unitId = formIndexToUnitId.get(i);
              if (!unitId) return;
              const val = row.units[i];
              prices.push({
                priceListId: row.priceListId,
                itemUnitId: unitId,
                unitPrice: val && val.trim() ? val.trim() : null,
                currencyCode,
              });
            });
          }
          await setItemGroupPrices(itemId, prices);
        } catch {
          /* group prices are non-blocking — set them later from Price lists */
        }
      }
      // Persist any translations typed on the CREATE form (deferred until we had an
      // item id). Non-blocking: the item is already saved; they're editable later.
      if (!edit && itemId && Object.keys(pendingTranslations).length > 0) {
        try {
          for (const [locale, fieldsMap] of Object.entries(pendingTranslations)) {
            const payloadFields: Record<string, string | null> = {};
            let hasAny = false;
            for (const k of ['name', 'description']) {
              const v = fieldsMap[k];
              payloadFields[k] = v && v.trim() ? v : null;
              if (v && v.trim()) hasAny = true;
            }
            if (hasAny) {
              await api().put('/cms/translations', { entityType: 'item', entityId: itemId, locale, fields: payloadFields });
            }
          }
        } catch {
          /* translations are non-blocking — add them later from the item's edit page */
        }
      }
      // The bundle's contents typed on the CREATE form, now that there is an id
      // to hang them on. Loud on failure like opening stock below: a bundle with
      // an empty box looks fine on the product page and ships nothing when sold.
      const comboLines = comboRowsToPayload(comboDraft);
      if (!edit && itemId && comboLines.length > 0) {
        try {
          await api().put(`/combos/by-item/${itemId}`, { lines: comboLines });
        } catch {
          toast.error(tCombos('saveFailed', { defaultValue: 'Could not save the bundle.' }));
        }
      }
      // Opening stock typed on the CREATE form: post it as an ordinary increase
      // now that the item has an id. Loud on failure — unlike prices/translations
      // a missing quantity is invisible on the item, so say so instead of
      // silently swallowing it (the item itself is already saved either way).
      if (!edit && itemId && hasOpeningStock(opening)) {
        try {
          const body: Record<string, unknown> = {
            itemId,
            warehouseId: opening.warehouseId,
            quantity: safeDecimal(normalizeNumericInput(opening.quantity)).toDecimalPlaces(4).toString(),
            note: t('form.stock.openingNote', { defaultValue: 'Opening stock' }),
          };
          const cost = normalizeNumericInput(opening.unitCost).trim();
          // In dollars, like every cost: a dinar figure is converted at today's
          // rate (kept as typed only when there is no rate to convert with).
          const costCcy = opening.currencyCode || COST_CURRENCY;
          const costUsd = cost !== '' && safeDecimal(cost).gte(0)
            ? toCostCurrency(safeDecimal(cost).toDecimalPlaces(4).toString(), costCcy) : '';
          if (cost !== '' && safeDecimal(cost).gte(0)) {
            body.unitCost = costUsd || safeDecimal(cost).toDecimalPlaces(4).toString();
            body.unitCostCurrencyCode = costUsd ? COST_CURRENCY : costCcy;
          } else if (canCost && purchaseValue) {
            // No cost typed on the opening stock: the purchase price above is it.
            body.unitCost = purchaseValue;
            body.unitCostCurrencyCode = purchaseCurrency;
          }
          await api().post('/stock/adjust', body);
        } catch {
          toast.error(ti('itemStock.openingFailed'));
        }
      }
      // A changed purchase price becomes the warehouses' average cost too — the
      // figure the stock page, the stock value and every new sale's profit read.
      if (edit && itemId && canCost && canSetCost && purchase.dirty && purchaseValue) {
        try {
          const r = await api().post<{ updated: number }>('/stock/cost', {
            itemId, unitCost: purchaseValue, currencyCode: purchaseCurrency,
          });
          if (r.data.updated > 0) {
            toast.success(t('form.purchaseCostUpdated', { defaultValue: 'Average cost updated in {{n}} warehouse(s)', n: r.data.updated }));
          }
        } catch {
          toast.error(t('form.purchaseCostFailed', { defaultValue: 'The item was saved, but the warehouses\' average cost could not be updated.' }));
        }
      }
      navigate(`/inventory/items/${itemId}`);
    } catch (err) {
      const body = (err as {
        response?: {
          data?: {
            code?: string;
            cause?: { code?: string; barcode?: string; itemCode?: string | null; itemName?: string | null };
          };
        };
      }).response?.data;
      const code = body?.code;
      // A barcode already owned by another product is the one save failure the
      // operator can actually fix — name the clashing item instead of a generic
      // "could not save", and send them to the tab that holds the offending field.
      if (body?.cause?.code === 'barcode_taken') {
        setTab('barcode');
        setError(t('form.extraBarcodes.taken', {
          barcode: body.cause.barcode ?? '',
          itemCode: body.cause.itemCode ?? '?',
          itemName: body.cause.itemName ?? '?',
          defaultValue: '{{barcode}} already belongs to {{itemCode}} — {{itemName}}.',
        }));
      } else if (code === 'conflict') setError(t('errors.duplicateCode'));
      else if (code === 'unprocessable') setError(t('errors.unprocessable'));
      else setError(tc('errors:server'));
    } finally {
      setSaving(false);
    }
  };

  if (!ready || !form) {
    return (
      <Card className="p-6">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="mt-4 h-64 w-full" />
      </Card>
    );
  }

  const categoryOptions = flattenCategoryOptions(categories);
  const NONE_OPT = '__none__';
  const categoryCombo: ComboboxOption[] = [
    { value: NONE_OPT, label: t('create.categoryNone', { defaultValue: '— None —' }) },
    ...categoryOptions.map((o) => ({ value: o.id, label: o.label })),
  ];
  const brandCombo: ComboboxOption[] = [
    { value: NONE_OPT, label: t('create.brandNone', { defaultValue: '— None —' }) },
    ...brands.map((b) => ({ value: b.id, label: b.title })),
  ];
  const selectClass = 'block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg';
  // Active units of measure (sorted) for the base/pack pickers. Always include
  // the field's current value so existing items stay selectable even if their
  // unit isn't (or no longer) in the active master list.
  const activeUomNames = uoms.filter((u) => u.isActive).sort((a, b) => a.sort - b.sort).map((u) => u.name);
  const uomOptions = (current: string | undefined): string[] => {
    const set = new Set(activeUomNames);
    if (current && current.trim()) set.add(current.trim());
    return [...set];
  };
  const sectionTitle = (Icon: LucideIcon, text: string): JSX.Element => (
    <h2 className="mb-4 flex items-center gap-2.5 text-sm font-bold uppercase tracking-wide text-fg-muted">
      <span className="grid h-7 w-7 place-items-center rounded-lg bg-brand/10 text-brand">
        <Icon className="h-4 w-4" aria-hidden />
      </span>
      {text}
    </h2>
  );

  // AI-generate the product description from the item's name + category + brand.
  const generateDescription = async (): Promise<void> => {
    if (!form.name?.trim()) { toast.error(t('ai.needName', { defaultValue: 'Enter a product name first.' })); return; }
    setAiBusy('description');
    try {
      const cat = categoryCombo.find((o) => o.value === form.categoryId);
      const brand = brandCombo.find((o) => o.value === form.brandId);
      const r = await aiGenerate({
        task: 'description',
        name: form.name,
        category: cat && cat.value !== NONE_OPT ? cat.label : undefined,
        brand: brand && brand.value !== NONE_OPT ? brand.label : undefined,
        locale: i18n.language.startsWith('ar') ? 'ar' : 'en',
      });
      if (r.text) setField({ description: r.text });
      else toast.error(t('ai.failed', { defaultValue: 'AI generation failed. Try again.' }));
    } catch {
      toast.error(t('ai.failed', { defaultValue: 'Could not generate — check Settings → AI.' }));
    } finally {
      setAiBusy(null);
    }
  };
  /**
   * Fill one option's Arabic side from its English side.
   *
   * One request PER VALUE, sent together. Handing the model the whole list in a
   * single call is cheaper, but it can come back with a different number of
   * lines — and these pair to English BY POSITION, so one dropped or merged
   * line silently mislabels every value after it. Per value, the pairing cannot
   * drift; a value the model fails on keeps its English text.
   */
  const translateOptionGroup = async (i: number): Promise<void> => {
    const g = optionGroups[i];
    if (!g) return;
    const name = g.name.trim();
    const values = splitOptionValues(g.values);
    if (!name && values.length === 0) {
      toast.error(t('form.variants.translateEmpty', { defaultValue: 'Type the English option first.' }));
      return;
    }
    if (values.length > AI_TRANSLATE_MAX) {
      toast.error(t('form.variants.translateTooMany', { count: AI_TRANSLATE_MAX, defaultValue: 'Up to {{count}} values at a time.' }));
      return;
    }
    setAiGroupBusy(i);
    try {
      const [nameRes, ...valueRes] = await Promise.all([
        name ? aiGenerate({ task: 'translate', source: name, targetLang: 'ar' }) : Promise.resolve(null),
        ...values.map((v) => aiGenerate({ task: 'translate', source: v, targetLang: 'ar' })),
      ]);
      setOptionGroup(i, {
        ...(nameRes?.text?.trim() ? { nameAr: nameRes.text.trim() } : {}),
        ...(values.length
          ? { valuesAr: values.map((v, k) => valueRes[k]?.text?.trim() || v).join('، ') }
          : {}),
      });
      toast.success(t('form.variants.translated', { defaultValue: 'Translated — check it, then Generate.' }));
    } catch {
      toast.error(t('ai.failed', { defaultValue: 'Could not generate — check Settings → AI.' }));
    } finally {
      setAiGroupBusy(null);
    }
  };

  /** Arabic for variant rows typed by hand — generated ones already carry it.
   *  Only fills the blanks, so a label someone wrote themselves is never lost. */
  const translateVariantNames = async (): Promise<void> => {
    const todo = (form.variants ?? [])
      .map((v, i) => ({ v, i }))
      .filter(({ v }) => v.name.trim() && !v.nameAr?.trim());
    if (todo.length === 0) {
      toast.error(t('form.variants.translateNothing', { defaultValue: 'Every variant already has an Arabic name.' }));
      return;
    }
    if (todo.length > AI_TRANSLATE_MAX) {
      toast.error(t('form.variants.translateTooMany', { count: AI_TRANSLATE_MAX, defaultValue: 'Up to {{count}} values at a time.' }));
      return;
    }
    setAiNamesBusy(true);
    try {
      const res = await Promise.all(
        todo.map(({ v }) => aiGenerate({ task: 'translate', source: v.name.trim(), targetLang: 'ar' })),
      );
      setForm((f) => {
        if (!f) return f;
        const next = [...(f.variants ?? [])];
        todo.forEach(({ i }, k) => {
          const text = res[k]?.text?.trim();
          const row = next[i];
          if (text && row) next[i] = { ...row, nameAr: text };
        });
        return { ...f, variants: next };
      });
      toast.success(t('form.variants.translated', { defaultValue: 'Translated — check it, then Generate.' }));
    } catch {
      toast.error(t('ai.failed', { defaultValue: 'Could not generate — check Settings → AI.' }));
    } finally {
      setAiNamesBusy(false);
    }
  };

  /**
   * The mirror of translateVariantNames, for a catalog whose own working
   * language ISN'T English (a shop that always types variant names in Arabic,
   * as a migrated catalog often does) — fills blank English overrides so an
   * English-browsing shopper stops seeing the Arabic label. Only fills the
   * blanks, so a label someone wrote themselves is never lost.
   */
  const translateVariantNamesEn = async (): Promise<void> => {
    const todo = (form.variants ?? [])
      .map((v, i) => ({ v, i }))
      .filter(({ v }) => v.name.trim() && !v.nameEn?.trim());
    if (todo.length === 0) {
      toast.error(t('form.variants.translateEnNothing', { defaultValue: 'Every variant already has an English name.' }));
      return;
    }
    if (todo.length > AI_TRANSLATE_MAX) {
      toast.error(t('form.variants.translateTooMany', { count: AI_TRANSLATE_MAX, defaultValue: 'Up to {{count}} values at a time.' }));
      return;
    }
    setAiNamesEnBusy(true);
    try {
      const res = await Promise.all(
        todo.map(({ v }) => aiGenerate({ task: 'translate', source: v.name.trim(), targetLang: 'en' })),
      );
      setForm((f) => {
        if (!f) return f;
        const next = [...(f.variants ?? [])];
        todo.forEach(({ i }, k) => {
          const text = res[k]?.text?.trim();
          const row = next[i];
          if (text && row) next[i] = { ...row, nameEn: text };
        });
        return { ...f, variants: next };
      });
      toast.success(t('form.variants.translated', { defaultValue: 'Translated — check it, then Generate.' }));
    } catch {
      toast.error(t('ai.failed', { defaultValue: 'Could not generate — check Settings → AI.' }));
    } finally {
      setAiNamesEnBusy(false);
    }
  };

  /** How many rows Generate would make — shown so the count is not a surprise. */
  const optionSizes = optionGroups.map((g) => splitOptionValues(g.values).length).filter((n) => n > 0);
  const plannedVariantCount = optionSizes.length ? optionSizes.reduce((a, b) => a * b, 1) : 0;

  // Small muted icon beside a field label.
  const fieldLabel = (Icon: LucideIcon, text: string): JSX.Element => (
    <span className="inline-flex items-center gap-1.5">
      <Icon className="h-3.5 w-3.5 text-fg-subtle" aria-hidden />
      {text}
    </span>
  );

  return (
    <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">
      <header className="flex items-center gap-3">
        <Link
          to={edit && initial ? `/inventory/items/${initial.id}` : '/inventory/items'}
          className="inline-flex h-9 items-center gap-1.5 rounded-md border border-border bg-bg-subtle px-3 text-sm font-semibold text-fg-muted hover:text-fg"
        >
          <ArrowLeft className="h-4 w-4 rtl:scale-x-[-1]" aria-hidden />
          {tc('actions.back', { defaultValue: 'Back' })}
        </Link>
        <h1 className="text-2xl font-semibold">
          {edit && initial
            ? t('edit.title', { code: initial.code, name: initial.name })
            : t('create.title')}
        </h1>
      </header>

      {/* onKeyDown: a scanner types the barcode and then sends Enter, which in a
          form with a Save button saves the product and leaves the page — the
          scan landed in the field and the edit went with it. Enter never means
          "save" here; the Save button below does, and it is always on screen. */}
      <form className="flex flex-col gap-4" onSubmit={onSubmit} onKeyDown={blockImplicitSubmit} noValidate>
        {/* dir: Radix Tabs stamp dir="ltr" on everything inside them unless told;
            told the page's direction (useLayoutDirection). */}
        <Tabs value={tab} onValueChange={setTab} dir={dir}>
          <TabsList className="h-auto flex-wrap justify-start gap-1">
            <TabsTrigger value="general" className="gap-1.5"><Package className="h-4 w-4" aria-hidden />{t('form.sections.basics', { defaultValue: 'Basics' })}</TabsTrigger>
            <TabsTrigger value="pricing" className="gap-1.5"><Tag className="h-4 w-4" aria-hidden />{t('form.sections.pricing', { defaultValue: 'Pricing' })}</TabsTrigger>
            <TabsTrigger value="barcode" className="gap-1.5"><ScanLine className="h-4 w-4" aria-hidden />{t('create.barcode')}</TabsTrigger>
            {canUploadImg && <TabsTrigger value="images" className="gap-1.5"><ImageIcon className="h-4 w-4" aria-hidden />{t('create.images', { defaultValue: 'Images' })}</TabsTrigger>}
            <TabsTrigger value="options" className="gap-1.5"><ToggleRight className="h-4 w-4" aria-hidden />{t('form.sections.options', { defaultValue: 'Options' })}</TabsTrigger>
            {variantsEnabled && <TabsTrigger value="variants" className="gap-1.5"><Shapes className="h-4 w-4" aria-hidden />{t('form.sections.variants', { defaultValue: 'Variants' })}</TabsTrigger>}
            {/* Recipe: edit mode only (a brand-new item has no id to hang a BOM
                on) and products only — a service has nothing to assemble. */}
            {manufacturingEnabled && canReadMfg && edit && form.itemType === 'product' && (
              <TabsTrigger value="recipe" className="gap-1.5"><Factory className="h-4 w-4" aria-hidden />{t('form.sections.recipe', { defaultValue: 'Recipe' })}</TabsTrigger>
            )}
            {/* Bundle: products only. Available while CREATING too — the box is
                held on the form and saved the moment the product exists. */}
            {combosEnabled && form.itemType === 'product' && (
              <TabsTrigger value="combo" className="gap-1.5"><Package className="h-4 w-4" aria-hidden />{t('form.sections.combo', { defaultValue: 'Bundle' })}</TabsTrigger>
            )}
            <TabsTrigger value="storefront" className="gap-1.5"><MonitorPlay className="h-4 w-4" aria-hidden />{t('form.sections.storefront', { defaultValue: 'Storefront / app' })}</TabsTrigger>
            {canReadStock && <TabsTrigger value="stock" className="gap-1.5"><Boxes className="h-4 w-4" aria-hidden />{t('form.sections.stock', { defaultValue: 'Stock movements' })}</TabsTrigger>}
          </TabsList>

          <TabsContent value="general" className="mt-4 flex flex-col gap-4">
        {/* Basics */}
        <Card className="p-6">
          {sectionTitle(Package, t('form.sections.basics', { defaultValue: 'Basics' }))}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField id="it-code" label={fieldLabel(Hash, t('create.code'))} required>
              <div className="flex gap-2">
                <Input
                  id="it-code"
                  value={form.code}
                  onChange={(e) => setField({ code: e.target.value })}
                  required
                  autoFocus={!edit}
                  className="flex-1"
                />
                <Button type="button" variant="secondary" onClick={onAutoSku} loading={generatingSku} title={t('create.autoSkuTitle')}>
                  {t('create.autoSku')}
                </Button>
              </div>
            </FormField>
            <FormField id="it-name" label={fieldLabel(Type, t('create.name'))} required>
              <Input id="it-name" value={form.name} onChange={(e) => setField({ name: e.target.value })} required />
            </FormField>
            <FormField id="it-type" label={fieldLabel(Shapes, t('create.type'))} required>
              <select
                id="it-type"
                className={selectClass}
                value={form.itemType}
                onChange={(e) =>
                  setField({
                    itemType: e.target.value as ItemType,
                    // A service has no stock; a product counts it (product → service → product
                    // used to come back untracked).
                    isInventoryTracked: e.target.value === 'product',
                  })
                }
              >
                {ITEM_TYPES.map((type) => (
                  <option key={type} value={type}>{t(`type.${type}`)}</option>
                ))}
              </select>
            </FormField>
            <FormField id="it-category" label={fieldLabel(FolderTree, t('create.category', { defaultValue: 'Category' }))}>
              <Combobox
                id="it-category"
                options={categoryCombo}
                value={form.categoryId ?? undefined}
                onChange={(v) => setField({ categoryId: v && v !== NONE_OPT ? v : undefined })}
                placeholder={t('create.searchCategory', { defaultValue: 'Search category…' })}
                ariaLabel={t('create.category', { defaultValue: 'Category' })}
              />
            </FormField>
            {multiCatEnabled && (
              <FormField
                id="it-extra-categories"
                label={fieldLabel(FolderTree, t('create.extraCategories', { defaultValue: 'Additional categories' }))}
              >
                <Combobox
                  id="it-extra-categories"
                  options={categoryOptions
                    .filter((o) => o.id !== form.categoryId && !(form.extraCategoryIds ?? []).includes(o.id))
                    .map((o) => ({ value: o.id, label: o.label }))}
                  onChange={(v) => {
                    if (v && v !== NONE_OPT && !(form.extraCategoryIds ?? []).includes(v)) {
                      setField({ extraCategoryIds: [...(form.extraCategoryIds ?? []), v] });
                    }
                  }}
                  clearOnSelect
                  placeholder={t('create.extraCategoriesAdd', { defaultValue: 'Add a category…' })}
                  ariaLabel={t('create.extraCategories', { defaultValue: 'Additional categories' })}
                />
                {(form.extraCategoryIds ?? []).length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {(form.extraCategoryIds ?? []).map((id) => (
                      <span
                        key={id}
                        className="inline-flex items-center gap-1 rounded-full border border-border bg-bg-subtle px-2.5 py-1 text-xs text-fg"
                      >
                        {categoryOptions.find((o) => o.id === id)?.label ?? id}
                        <button
                          type="button"
                          aria-label={tc('actions.remove', { defaultValue: 'Remove' })}
                          className="text-fg-subtle transition-colors hover:text-danger"
                          onClick={() =>
                            setField({ extraCategoryIds: (form.extraCategoryIds ?? []).filter((x) => x !== id) })
                          }
                        >
                          <X className="h-3 w-3" aria-hidden />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </FormField>
            )}
            <FormField id="it-brand" label={fieldLabel(Bookmark, t('create.brand', { defaultValue: 'Brand' }))}>
              <Combobox
                id="it-brand"
                options={brandCombo}
                value={form.brandId ?? undefined}
                onChange={(v) => setField({ brandId: v && v !== NONE_OPT ? v : undefined })}
                placeholder={t('create.searchBrand', { defaultValue: 'Search brand…' })}
                ariaLabel={t('create.brand', { defaultValue: 'Brand' })}
              />
            </FormField>
            <FormField id="it-uom" label={fieldLabel(Ruler, t('create.unitOfMeasure'))}>
              <div className="flex gap-2">
                <select id="it-uom" className={`${selectClass} flex-1`} value={form.unitOfMeasure} onChange={(e) => setField({ unitOfMeasure: e.target.value })}>
                  {uomOptions(form.unitOfMeasure).map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
                {addUnitButton('base')}
              </div>
              {addUnitForm('base')}
            </FormField>
          </div>
        </Card>

        {/* Description (rich text) */}
        <Card className="p-6">
          <div className="flex items-start justify-between gap-3">
            {sectionTitle(FileText, t('form.sections.description', { defaultValue: 'Description' }))}
            {aiEnabled && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={aiBusy === 'description'}
                disabled={!form.name?.trim() || aiBusy !== null}
                onClick={() => void generateDescription()}
              >
                <Sparkles className="h-4 w-4" aria-hidden />
                {t('ai.generate', { defaultValue: 'Generate with AI' })}
              </Button>
            )}
          </div>
          <RichTextEditor
            value={form.description ?? ''}
            onChange={(html) => setField({ description: html })}
            ariaLabel={t('form.sections.description', { defaultValue: 'Description' })}
            placeholder={t('form.descriptionPlaceholder', { defaultValue: 'Describe this item…' })}
          />
        </Card>

        {/* Translations — name + description in the tenant's other languages. The
            fields above are the default language; this writes overlays. Shown on
            CREATE too (deferred: values are held here and saved with the item). */}
        <Card className="p-6">
          {sectionTitle(Languages, t('form.sections.translations', { defaultValue: 'Translations' }))}
          {initial ? (
            <TranslationEditor
              entityType="item"
              entityId={initial.id}
              fields={[
                { key: 'name', label: t('create.name') },
                { key: 'description', label: t('form.sections.description', { defaultValue: 'Description' }), type: 'richtext' },
              ]}
              aiSources={aiEnabled ? { name: form.name, description: form.description ?? '' } : undefined}
            />
          ) : (
            <TranslationEditor
              entityType="item"
              deferred
              value={pendingTranslations}
              onChange={setPendingTranslations}
              fields={[
                { key: 'name', label: t('create.name') },
                { key: 'description', label: t('form.sections.description', { defaultValue: 'Description' }), type: 'richtext' },
              ]}
              aiSources={aiEnabled ? { name: form.name, description: form.description ?? '' } : undefined}
            />
          )}
        </Card>

          </TabsContent>

          <TabsContent value="pricing" className="mt-4 flex flex-col gap-4">
        {/* Pricing */}
        <Card className="p-6">
          {sectionTitle(Tag, t('form.sections.pricing', { defaultValue: 'Pricing' }))}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField id="it-saleprice" label={fieldLabel(Tag, t('create.salePrice'))}>
              <NumberInput id="it-saleprice" value={form.salePrice ?? ''} onValueChange={(raw) => setField({ salePrice: raw })} placeholder="0.00" />
            </FormField>
            <FormField id="it-salecur" label={fieldLabel(Coins, t('create.saleCurrency'))}>
              <CurrencySelect id="it-salecur" value={form.saleCurrencyCode ?? ''} onChange={(c) => setField({ saleCurrencyCode: c })} includeEmpty />
            </FormField>
            {/* «سعر الشراء» per piece — only for those who may see cost. Saved on
                the item and, for those who may adjust stock, it becomes the
                average cost in every warehouse holding the item. Purchase bills
                keep averaging their own prices in afterwards. */}
            {canCost && (
              <>
                <FormField
                  id="it-purchase"
                  label={fieldLabel(Coins, t('form.purchasePrice', { defaultValue: 'Purchase price (per piece)' }))}
                  helperText={canSetCost
                    ? t('form.purchaseHint', { defaultValue: 'Saved on the item and becomes the average cost in every warehouse that holds it.' })
                    : t('form.purchaseHintNoStock', { defaultValue: 'Saved on the item only — changing the warehouses\' average cost needs the stock-adjust right.' })}
                >
                  <NumberInput
                    id="it-purchase"
                    value={purchase.price}
                    onValueChange={(raw) => setPurchase((p) => ({ ...p, price: raw, dirty: true }))}
                    placeholder="0.00"
                  />
                </FormField>
                <FormField id="it-purchasecur" label={fieldLabel(Coins, t('form.purchaseCurrency', { defaultValue: 'Purchase currency' }))}>
                  {/* Saved in dollars, always. Typing in dinars is allowed — it is
                      converted at today's rate and the dollar figure is shown. */}
                  <div className="flex flex-col gap-1">
                    <div className="inline-flex w-fit rounded-lg border border-border p-0.5" role="radiogroup">
                      {[COST_CURRENCY, ...Object.keys(costRates).filter((c) => c === 'IQD')].map((c) => (
                        <button
                          key={c}
                          type="button"
                          role="radio"
                          aria-checked={purchase.entry === c}
                          onClick={() => setPurchase((p) => ({ ...p, entry: c, dirty: true }))}
                          className={`rounded-md px-3 py-1 text-sm font-semibold ${purchase.entry === c ? 'bg-brand text-brand-contrast' : 'text-fg-muted hover:text-fg'}`}
                        >
                          {c === COST_CURRENCY ? c : t('form.purchaseTypeIn', { defaultValue: 'Type in {{ccy}}', ccy: c })}
                        </button>
                      ))}
                    </div>
                    {purchase.entry !== COST_CURRENCY && purchaseTyped && (
                      <span className="text-xs text-fg-muted">
                        {purchaseValue
                          ? t('form.purchaseConverted', {
                              defaultValue: '= {{usd}} (1 USD = {{rate}} {{ccy}}) — saved in dollars',
                              usd: formatMoney(purchaseValue, { locale: i18n.language, currency: COST_CURRENCY }),
                              rate: String(costRates[purchase.entry] ?? ''),
                              ccy: purchase.entry,
                            })
                          : t('form.purchaseNoRate', { defaultValue: 'No exchange rate for {{ccy}} today — type the price in dollars.', ccy: purchase.entry })}
                      </span>
                    )}
                  </div>
                </FormField>
                {stockCosts.length > 0 && (
                  <p className="text-xs text-fg-muted md:col-span-2">
                    {t('form.currentAvgCost', { defaultValue: 'Average cost now' })}:{' '}
                    {stockCosts.map((s) => (
                      <span key={s.name} className="me-3 inline-block">
                        {s.name} <span dir="ltr" className="font-mono">{formatMoney(s.avgCost, { locale: i18n.language, currency: s.currency })}</span>
                      </span>
                    ))}
                  </p>
                )}
              </>
            )}
            {taxEnabled && (
              <FormField id="it-tax" label={fieldLabel(Percent, t('form.taxRate', { defaultValue: 'Tax rate (0–1)' }))}>
                <Input id="it-tax" value={form.taxRate ?? ''} onChange={(e) => setField({ taxRate: e.target.value })} inputMode="decimal" placeholder="0.15" />
              </FormField>
            )}
          </div>
        </Card>

        {/* Price per customer group — a sale price PER SELLING UNIT (base + each
            pack/carton) for each price list, entered directly in that group's own
            currency (never converted). Only shown when the pricing module is on
            AND there's ≥1 active price list. */}
        {pricingEnabled && groupRows.length > 0 && (
          <Card className="p-6">
            {sectionTitle(Users, t('groupPrices.title', { defaultValue: 'Price per customer group' }))}
            <p className="mb-3 text-xs text-fg-muted">
              {t('groupPrices.hint', {
                defaultValue:
                  'Set a sale price for each customer group in that group’s own currency — one per selling unit (base plus any pack/carton). Prices are entered directly and are never converted. Leave blank to clear a unit’s price.',
              })}
            </p>
            <div className="grid grid-cols-1 gap-2.5 lg:grid-cols-2">
              {groupRows.map((row) => {
                const units = form.units ?? [];
                const baseLabel = `1 ${form.unitOfMeasure} · ${t('groupPrices.baseUnit', { defaultValue: 'Base unit' })}`;
                const unitLabel = (u: ItemUnitInput, i: number): string =>
                  (u.name
                    ? `${u.factor?.trim() ? u.factor : '?'} ${form.unitOfMeasure} · ${u.name}`
                    : `${t('groupPrices.unit', { defaultValue: 'Unit' })} ${i + 1}`) + hiddenMark(u);
                const priceRows: Array<{ key: string; icon: LucideIcon; label: string; value: string; set: (v: string) => void }> = [
                  { key: 'base', icon: Tag, label: baseLabel, value: row.base, set: (v) => setGroupBase(row.priceListId, v) },
                  ...units.map((u, i) => ({ key: `u${i}`, icon: Boxes, label: unitLabel(u, i), value: row.units[i] ?? '', set: (v: string) => setGroupUnit(row.priceListId, i, v) })),
                ];
                return (
                  <div key={row.priceListId} className="rounded-xl border border-border bg-bg-subtle/40 p-3">
                    {/* Header: group name + its FIXED currency (from the price list, not editable). */}
                    <div className="mb-2.5 flex items-center justify-between gap-2 border-b border-border/60 pb-2">
                      <span className="flex min-w-0 items-center gap-1.5 text-sm font-semibold">
                        <Users className="h-3.5 w-3.5 shrink-0 text-fg-muted" aria-hidden />
                        <span className="truncate">{row.priceListName}</span>
                      </span>
                      <span
                        className="shrink-0 rounded-md border border-border bg-bg px-2 py-0.5 font-mono text-[0.7rem] font-semibold text-brand"
                        title={t('groupPrices.fixedCurrency', { defaultValue: 'Currency is set by the customer group.' })}
                      >
                        {row.currencyCode}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1.5">
                      {priceRows.map((pr) => {
                        const Ico = pr.icon;
                        return (
                          <div key={pr.key} className="flex items-center justify-between gap-3">
                            <label htmlFor={`gp-${pr.key}-${row.priceListId}`} className="flex min-w-0 items-center gap-1.5 text-xs text-fg-muted">
                              <Ico className="h-3.5 w-3.5 shrink-0" aria-hidden />
                              <span className="truncate">{pr.label}</span>
                            </label>
                            <div className="relative w-32 shrink-0">
                              <NumberInput
                                id={`gp-${pr.key}-${row.priceListId}`}
                                value={pr.value}
                                onValueChange={pr.set}
                                currency={row.currencyCode}
                                placeholder="0.00"
                                className="h-9 pe-10 text-end font-mono tabular-nums"
                              />
                              <span className="pointer-events-none absolute inset-y-0 end-2.5 flex items-center text-[0.65rem] font-medium text-fg-subtle">{row.currencyCode}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Units & pricing */}
        <Card className="p-6">
          {sectionTitle(Boxes, t('form.units.section', { defaultValue: 'Units & pricing' }))}
          <p className="mb-3 text-xs text-fg-muted">
            {t('form.units.hint', {
              defaultValue:
                'Sell this item in larger units (e.g. a pack of 12). Factor = how many base units it holds. Add as many levels as you need.',
            })}
          </p>
          <div className="rounded-lg border border-border bg-bg-subtle px-3 py-2 text-sm">
            <span className="font-semibold">{t('form.units.base', { defaultValue: 'Base unit' })}:</span>{' '}
            1 {form.unitOfMeasure}
            {form.salePrice && form.saleCurrencyCode ? ` = ${formatMoney(form.salePrice, { locale: i18n.language, currency: form.saleCurrencyCode })}` : ''}
          </div>
          {(form.units ?? []).map((u, i) => {
            const hidden = u.isActive === false;
            return (
              <div
                key={i}
                className={`mt-3 rounded-lg border p-3 ${hidden ? 'border-dashed border-warning bg-bg-subtle' : 'border-border'}`}
              >
                {/* Header: which unit, whether it is on sale, and remove. The
                    remove button used to float at the bottom of the card, and the
                    fields were bottom-aligned in one grid, so a hint under one input
                    pushed its label out of line with the others. */}
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex min-w-0 items-center gap-2 text-sm font-semibold">
                    <Boxes className="h-4 w-4 flex-none text-fg-subtle" aria-hidden />
                    <span className="truncate">
                      {u.name || t('form.units.unitN', { n: i + 1, defaultValue: 'Unit {{n}}' })}
                    </span>
                    {u.factor?.trim() ? (
                      <span className="flex-none text-xs font-normal text-fg-muted" dir="ltr">
                        × {u.factor}
                      </span>
                    ) : null}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Button
                      type="button"
                      variant={hidden ? 'secondary' : 'ghost'}
                      size="sm"
                      aria-pressed={!hidden}
                      onClick={() => toggleUnitShown(i)}
                      className={hidden ? 'text-warning' : 'text-success'}
                      title={hidden
                        ? t('form.units.showHint', { defaultValue: 'Offer this unit again everywhere.' })
                        : t('form.units.hideHint', {
                          defaultValue: 'Stop offering this unit everywhere — the app, the website, the till, reps and new documents. Its prices and barcodes are kept.',
                        })}
                    >
                      {hidden ? <EyeOff className="h-4 w-4" aria-hidden /> : <Eye className="h-4 w-4" aria-hidden />}
                      {hidden
                        ? t('form.units.hidden', { defaultValue: 'Hidden' })
                        : t('form.units.shown', { defaultValue: 'Shown' })}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeUnit(i)}
                      aria-label={tc('actions.remove', { defaultValue: 'Remove' })}
                      title={tc('actions.remove', { defaultValue: 'Remove' })}
                    >
                      <X className="h-4 w-4" aria-hidden />
                    </Button>
                  </div>
                </div>
                {hidden && (
                  <p className="mb-3 flex items-start gap-1.5 text-xs text-warning">
                    <EyeOff className="mt-0.5 h-3.5 w-3.5 flex-none" aria-hidden />
                    {t('form.units.hiddenNote', {
                      defaultValue:
                        'Hidden — not offered in the app, on the website, at the till, to reps or on new documents, and its barcodes do not scan. Prices and barcodes are kept; show it again any time.',
                    })}
                  </p>
                )}
                <div className="grid grid-cols-1 gap-3 md:grid-cols-12 md:items-start">
                  <div className="md:col-span-3">
                    <FormField id={`u-name-${i}`} label={t('form.units.name', { defaultValue: 'Unit' })}>
                      <div className="flex gap-2">
                        <select
                          id={`u-name-${i}`}
                          className={`${selectClass} flex-1`}
                          value={u.name}
                          onChange={(e) => setUnit(i, { name: e.target.value })}
                        >
                          <option value="">{t('form.units.selectUnit', { defaultValue: 'Select unit…' })}</option>
                          {uomOptions(u.name).map((n) => <option key={n} value={n}>{n}</option>)}
                        </select>
                        {addUnitButton(i)}
                      </div>
                      {addUnitForm(i)}
                    </FormField>
                  </div>
                  <div className="md:col-span-2">
                    <FormField
                      id={`u-factor-${i}`}
                      label={t('form.units.factor', { defaultValue: '× base units' })}
                      helperText={unitBreakdown(i)}
                    >
                      <Input
                        id={`u-factor-${i}`}
                        value={u.factor}
                        // Stored as ASCII, per lib/numerals: this is a plain Input,
                        // not a NumberInput, so nothing else normalises it — and an
                        // Arabic-keyboard "١٢" went to the API verbatim, where the
                        // factor pattern is ASCII-only, and took the WHOLE item save
                        // down with a 422 the user read as "it just doesn't save".
                        onChange={(e) => setUnit(i, { factor: normalizeNumericInput(e.target.value) })}
                        inputMode="decimal"
                        placeholder="12"
                      />
                    </FormField>
                  </div>
                  <div className="md:col-span-3">
                    <FormField id={`u-sale-${i}`} label={t('create.salePrice')}>
                      <NumberInput
                        id={`u-sale-${i}`}
                        value={u.salePrice ?? ''}
                        onValueChange={(raw) => setUnit(i, { salePrice: raw || undefined })}
                        placeholder="0.00"
                      />
                    </FormField>
                  </div>
                  {/* Purchase price is deliberately absent — see the Pricing card. */}
                  <div className="md:col-span-4">
                    <FormField id={`u-bc-${i}`} label={t('create.barcode')}>
                      <div className="flex gap-1.5">
                        <Input
                          id={`u-bc-${i}`}
                          value={u.barcode ?? ''}
                          onChange={(e) => setUnit(i, { barcode: e.target.value || undefined })}
                          maxLength={64}
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="secondary"
                          className="flex-none"
                          onClick={() => setUnit(i, { barcode: generateBarcodeValue(form.barcodeFormat ?? 'code128') })}
                          title={t('create.generateBarcode', { defaultValue: 'Generate barcode' })}
                          aria-label={t('create.generateBarcode', { defaultValue: 'Generate barcode' })}
                        >
                          <Barcode className="h-4 w-4" aria-hidden />
                        </Button>
                      </div>
                    </FormField>
                  </div>
                </div>
                {/* This pack's OTHER codes. The shop stocks one carton in ten
                    colours, each with its own EAN: without this the only way to
                    make them all scan was ten identical carton units, which is
                    what the cashier's unit list was drowning in. */}
                <div className="mt-3">
                  <FormField
                    id={`u-xbc-${i}`}
                    label={t('form.units.extraBarcodes', { defaultValue: 'Other codes for this unit' })}
                    helperText={t('form.units.extraBarcodesHint', {
                      defaultValue: 'Also scan as this unit — the same pack under another label. Separate with a space or comma.',
                    })}
                  >
                    <div className="flex flex-wrap items-center gap-1.5">
                      {(u.barcodes ?? []).map((b, bi) => (
                        <span
                          key={bi}
                          className="inline-flex items-center gap-1 rounded-full border border-border bg-bg-subtle px-2 py-0.5 font-mono text-xs"
                          dir="ltr"
                        >
                          {b}
                          <button
                            type="button"
                            onClick={() => setUnit(i, { barcodes: (u.barcodes ?? []).filter((_, k) => k !== bi) })}
                            aria-label={tc('actions.remove', { defaultValue: 'Remove' })}
                            className="text-fg-subtle hover:text-danger"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                      <Input
                        id={`u-xbc-${i}`}
                        className="min-w-[12rem] flex-1 font-mono sm:max-w-xs"
                        dir="ltr"
                        placeholder={t('form.units.addCode', { defaultValue: 'Add code, then Enter' })}
                        aria-label={t('form.units.extraBarcodes', { defaultValue: 'Other codes for this unit' })}
                        onKeyDown={(e) => {
                          if (e.key !== 'Enter') return;
                          e.preventDefault();
                          const el = e.target as HTMLInputElement;
                          addUnitCodes(i, el.value);
                          el.value = '';
                        }}
                        onPaste={(e) => {
                          const text = e.clipboardData.getData('text');
                          if (!/[;,\s]/.test(text.trim())) return;
                          e.preventDefault();
                          addUnitCodes(i, text);
                          (e.target as HTMLInputElement).value = '';
                        }}
                      />
                    </div>
                  </FormField>
                </div>
              </div>
            );
          })}
          <div className="mt-3">
            <Button type="button" variant="secondary" size="sm" onClick={addUnit}>
              <Plus className="h-4 w-4" aria-hidden />
              {t('form.units.add', { defaultValue: 'Add unit' })}
            </Button>
          </div>
        </Card>

          </TabsContent>

        {/* Images */}
        {canUploadImg && (
          <TabsContent value="images" className="mt-4 flex flex-col gap-4">
          <Card className="p-6">
            <div className="flex items-start justify-between gap-3">
              {sectionTitle(ImageIcon, t('create.images', { defaultValue: 'Images' }))}
              {imagegenEnabled && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={!form.name?.trim()}
                  onClick={() => setAiImgOpen(true)}
                >
                  <Sparkles className="h-4 w-4" aria-hidden />
                  {t('ai.imgButton', { defaultValue: 'Generate with AI' })}
                </Button>
              )}
            </div>
            <p className="mb-2 text-xs text-fg-muted">
              {t('form.imagesMultiHint', {
                defaultValue: 'Pick one image to crop, or several at once to add them all. The first is the main photo.',
              })}
            </p>
            <input
              ref={imgInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.length) void onPickFiles(e.target.files);
                if (e.target) e.target.value = '';
              }}
            />
            <div
              className={`flex flex-wrap gap-2 rounded-xl border-2 border-dashed p-3 transition ${
                dragOver ? 'border-brand bg-brand/5' : 'border-border/60'
              }`}
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                if (e.dataTransfer.files?.length) void onPickFiles(e.dataTransfer.files);
              }}
            >
              {images.map((g, i) => (
                <div key={g.key} className="relative h-24 w-24 overflow-hidden rounded-xl border border-border bg-bg-subtle">
                  <img src={g.url} alt="" className="h-full w-full object-contain" />
                  {i === 0 && (
                    <span className="absolute inset-x-0 bottom-0 bg-black/55 py-0.5 text-center text-[9px] font-bold uppercase tracking-wide text-white">
                      {t('create.primaryImage', { defaultValue: 'Main' })}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => onRemoveImage(g)}
                    aria-label={t('create.removeImage', { defaultValue: 'Remove' })}
                    className="absolute end-1 top-1 grid h-5 w-5 place-items-center rounded-full bg-black/55 text-white transition hover:bg-black/80"
                  >
                    <X className="h-3 w-3" aria-hidden />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => imgInputRef.current?.click()}
                aria-label={t('create.uploadImage', { defaultValue: 'Add image' })}
                className="grid h-24 w-24 place-items-center rounded-xl border border-dashed border-border bg-bg-subtle text-fg-muted transition hover:border-brand hover:text-fg"
              >
                <Plus className="h-6 w-6" aria-hidden />
              </button>
            </div>
          </Card>
          </TabsContent>
        )}

          <TabsContent value="barcode" className="mt-4 flex flex-col gap-4">
        {/* Barcode */}
        <Card className="p-6">
          {sectionTitle(ScanLine, t('create.barcode'))}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormField id="it-barcode" label={fieldLabel(Barcode, t('create.barcode'))}>
              <div className="flex gap-2">
                <Input id="it-barcode" value={form.barcode ?? ''} onChange={(e) => setField({ barcode: e.target.value })} maxLength={64} className="flex-1" />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setField({ barcode: generateBarcodeValue(form.barcodeFormat ?? 'code128') })}
                  title={t('create.generateBarcode', { defaultValue: 'Generate barcode' })}
                >
                  {t('create.generateBarcode', { defaultValue: 'Generate' })}
                </Button>
                <Button type="button" variant="secondary" onClick={() => form.code.trim() && setField({ barcode: form.code.trim() })} disabled={!form.code.trim()} title={t('create.useSkuAsBarcode')}>
                  {t('create.useSkuAsBarcode')}
                </Button>
              </div>
            </FormField>
            <FormField id="it-barcode-format" label={fieldLabel(ScanLine, t('create.barcodeFormat'))}>
              <select id="it-barcode-format" className={selectClass} value={form.barcodeFormat ?? 'code128'} onChange={(e) => setField({ barcodeFormat: e.target.value as BarcodeFormat })}>
                {BARCODE_FORMATS.map((fmt) => (
                  <option key={fmt} value={fmt}>{t(`barcodeFormat.${fmt}`)}</option>
                ))}
              </select>
            </FormField>
            <div className="md:col-span-2">
              <BarcodePreview value={form.barcode ?? ''} format={form.barcodeFormat ?? 'code128'} />
            </div>
          </div>
        </Card>

        {/* Extra barcodes — the SAME product under a second manufacturer label.
            Distinct from units (a different pack size at a different price) and
            from variants (a separately-priced/stocked option): these all resolve
            to this one item at this one price. */}
        <Card className="p-6">
          {sectionTitle(Barcode, t('form.extraBarcodes.title', { defaultValue: 'Extra barcodes' }))}
          <p className="mb-3 text-xs text-fg-muted">
            {t('form.extraBarcodes.hint', {
              defaultValue: 'Other codes that should scan to THIS product — e.g. the same item with a different colour or print run. Same product, same price, same stock. The barcode above stays the one that gets printed.',
            })}
          </p>
          <div className="flex flex-col gap-2">
            {(form.barcodes ?? []).map((b, i) => (
              <div key={i} className="flex items-center gap-2">
                <Input
                  id={`it-xbc-${i}`}
                  value={b}
                  onChange={(e) => setExtraBarcode(i, e.target.value)}
                  maxLength={64}
                  className="flex-1 font-mono"
                  dir="ltr"
                  aria-label={t('form.extraBarcodes.code', { defaultValue: 'Extra barcode' })}
                  placeholder={t('form.extraBarcodes.placeholder', { defaultValue: 'e.g. 222222' })}
                />
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => removeExtraBarcode(i)}
                  aria-label={tc('actions.remove', { defaultValue: 'Remove' })}
                  title={tc('actions.remove', { defaultValue: 'Remove' })}
                >
                  <Trash2 className="h-4 w-4" aria-hidden />
                </Button>
              </div>
            ))}
            {(form.barcodes ?? []).length === 0 && (
              <p className="text-xs text-fg-subtle">{t('form.extraBarcodes.empty', { defaultValue: 'No extra barcodes.' })}</p>
            )}
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="secondary" onClick={addExtraBarcode} disabled={(form.barcodes ?? []).length >= MAX_ITEM_BARCODES}>
                {t('form.extraBarcodes.add', { defaultValue: 'Add barcode' })}
              </Button>
              {/* Paste-many: someone with the codes written down as "111111 222222"
                  (or a column copied out of Excel) drops them in and gets rows. */}
              <Input
                id="it-xbc-paste"
                className="w-56 font-mono"
                dir="ltr"
                placeholder={t('form.extraBarcodes.paste', { defaultValue: 'Paste many, then Enter' })}
                aria-label={t('form.extraBarcodes.paste', { defaultValue: 'Paste many, then Enter' })}
                onKeyDown={(e) => {
                  if (e.key !== 'Enter') return;
                  e.preventDefault();
                  appendPastedBarcodes((e.target as HTMLInputElement).value);
                  (e.target as HTMLInputElement).value = '';
                }}
                onPaste={(e) => {
                  const text = e.clipboardData.getData('text');
                  if (!/[;,\s]/.test(text.trim())) return; // single code → let it type normally
                  e.preventDefault();
                  appendPastedBarcodes(text);
                }}
              />
            </div>
          </div>
        </Card>

        {/* All barcodes — the base unit plus every pack/carton unit. */}
        <Card className="p-6">
          {sectionTitle(Barcode, t('form.barcodes.all', { defaultValue: 'All barcodes' }))}
          <table className="w-full text-sm">
            <thead className="text-fg-subtle">
              <tr>
                <th className="py-1 text-start font-semibold">{t('form.units.unit', { defaultValue: 'Unit' })}</th>
                <th className="py-1 text-start font-semibold">{t('create.barcode')}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t border-border">
                <td className="py-1.5">
                  1 × {form.unitOfMeasure}{' '}
                  <span className="text-xs text-fg-subtle">({t('form.units.base', { defaultValue: 'Base unit' })})</span>
                </td>
                <td className="py-1.5 font-mono text-xs">{form.barcode?.trim() ? form.barcode : <span className="text-fg-subtle">—</span>}</td>
              </tr>
              {(form.units ?? []).map((u, i) => (
                <tr key={i} className="border-t border-border">
                  <td className="py-1.5">
                    {u.factor || '?'} × {form.unitOfMeasure}
                    {u.name ? <span className="text-xs text-fg-subtle"> ({u.name})</span> : null}
                    {unitBreakdown(i) ? (
                      <span className="text-xs text-fg-subtle"> {unitBreakdown(i)}</span>
                    ) : null}
                    {u.isActive === false ? (
                      <span className="ms-1.5 inline-flex items-center gap-1 text-xs font-medium text-warning">
                        <EyeOff className="h-3 w-3" aria-hidden />
                        {t('form.units.hidden', { defaultValue: 'Hidden' })}
                      </span>
                    ) : null}
                  </td>
                  <td className="py-1.5 font-mono text-xs">{u.barcode?.trim() ? u.barcode : <span className="text-fg-subtle">—</span>}</td>
                </tr>
              ))}
              {/* A pack's other labels belong on the pack's line, not among the
                  base unit's: this table is what an operator checks to answer
                  "what does this code ring up as?". */}
              {(form.units ?? []).flatMap((u, i) =>
                (u.barcodes ?? []).filter((b) => b.trim()).map((b, bi) => (
                  <tr key={`ux-${i}-${bi}`} className="border-t border-border">
                    <td className="py-1.5">
                      {u.factor || '?'} × {form.unitOfMeasure}
                      {u.name ? <span className="text-xs text-fg-subtle"> ({u.name})</span> : null}
                      <span className="text-xs text-fg-subtle"> · {t('form.extraBarcodes.title', { defaultValue: 'Extra barcodes' })}</span>
                    </td>
                    <td className="py-1.5 font-mono text-xs">{b}</td>
                  </tr>
                )),
              )}
              {(form.barcodes ?? []).filter((b) => b.trim()).map((b, i) => (
                <tr key={`x-${i}`} className="border-t border-border">
                  <td className="py-1.5">
                    1 × {form.unitOfMeasure}{' '}
                    <span className="text-xs text-fg-subtle">({t('form.extraBarcodes.title', { defaultValue: 'Extra barcodes' })})</span>
                  </td>
                  <td className="py-1.5 font-mono text-xs" dir="ltr">{b}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>

          </TabsContent>

          <TabsContent value="options" className="mt-4 flex flex-col gap-4">
        {/* Additional details (custom fields) */}
        {customFieldDefs.length > 0 && (
          <Card className="p-6">
            {sectionTitle(SlidersHorizontal, t('create.additionalDetails', { defaultValue: 'Additional details' }))}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {customFieldDefs.map((d) => (
                <CustomFieldInput key={d.id} def={d} value={form.customFields?.[d.code]} onChange={(v) => setCustom(d.code, v)} />
              ))}
            </div>
          </Card>
        )}

        {/* Options */}
        <Card className="p-6">
          {sectionTitle(ToggleRight, t('form.sections.options', { defaultValue: 'Options' }))}
          <div className="flex flex-col gap-2">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.isSellable} onChange={(e) => setField({ isSellable: e.target.checked })} />
              <span>{t('create.isSellable')}</span>
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.isPurchasable} onChange={(e) => setField({ isPurchasable: e.target.checked })} />
              <span>{t('create.isPurchasable')}</span>
            </label>
            {form.itemType === 'product' && (
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.isInventoryTracked ?? true} onChange={(e) => setField({ isInventoryTracked: e.target.checked })} />
                <span>{t('create.isInventoryTracked')}</span>
              </label>
            )}
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.earnsCashback ?? true} onChange={(e) => setField({ earnsCashback: e.target.checked })} />
              <span>{t('create.earnsCashback', { defaultValue: 'Earns cashback' })}</span>
            </label>
          </div>
        </Card>

          </TabsContent>

          <TabsContent value="variants" className="mt-4 flex flex-col gap-4">
        <Card className="p-6">
          {sectionTitle(Shapes, t('form.sections.variants', { defaultValue: 'Variants' }))}
          <p className="mb-3 text-xs text-fg-muted">{t('form.variants.hint', { defaultValue: 'Each variant is a sellable option with its own price, stock and barcode. Empty price falls back to the base price.' })}</p>
          <p className="text-sm font-medium">{t('form.variants.count', { count: (form.variants ?? []).length, defaultValue: '{{count}} variant(s)' })}</p>
          <div className="mt-3">
            <Button type="button" onClick={() => setVariantsOpen(true)}>
              <Shapes className="h-4 w-4" aria-hidden />
              {t('form.variants.manage', { defaultValue: 'Manage variants' })}
            </Button>
          </div>
        </Card>

        <Dialog open={variantsOpen} onOpenChange={setVariantsOpen}>
          <DialogContent className="flex max-h-[85vh] max-w-3xl flex-col gap-4 overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{t('form.sections.variants', { defaultValue: 'Variants' })}</DialogTitle>
            </DialogHeader>
        {/* Options builder — define option groups + values, then generate variant rows */}
        <Card className="p-6">
          {sectionTitle(SlidersHorizontal, t('form.variants.optionsTitle', { defaultValue: 'Build options' }))}
          <p className="mb-3 text-xs text-fg-muted">
            {t('form.variants.optionsHint', {
              defaultValue: 'Define options (e.g. Color: Red, Blue) then Generate to create a priced variant for every combination. Or add variants directly below.',
            })}
          </p>
          {optionGroups.map((g, i) => (
            <div key={i} className="mt-3 rounded-lg border border-border p-3">
              {/* English row */}
              <div className="grid grid-cols-1 gap-2 md:grid-cols-12 md:items-end">
                <div className="md:col-span-4">
                  <FormField id={`og-name-${i}`} label={`${t('form.variants.optionName', { defaultValue: 'Option name' })} · EN`}>
                    <Input id={`og-name-${i}`} value={g.name} onChange={(e) => setOptionGroup(i, { name: e.target.value })} placeholder={t('form.variants.optionNamePlaceholder', { defaultValue: 'e.g. Color' })} />
                  </FormField>
                </div>
                <div className="md:col-span-7">
                  <FormField id={`og-vals-${i}`} label={`${t('form.variants.optionValues', { defaultValue: 'Values (comma-separated)' })} · EN`}>
                    <Input id={`og-vals-${i}`} value={g.values} onChange={(e) => setOptionGroup(i, { values: e.target.value })} placeholder={t('form.variants.optionValuesPlaceholder', { defaultValue: 'e.g. Red, Blue, Green' })} />
                  </FormField>
                </div>
                <div className="flex md:col-span-1 md:justify-end">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setOptionGroups((gs) => gs.filter((_, idx) => idx !== i))} aria-label={tc('actions.remove', { defaultValue: 'Remove' })} title={tc('actions.remove', { defaultValue: 'Remove' })}>
                    <X className="h-4 w-4" aria-hidden />
                  </Button>
                </div>
              </div>
              {/* Arabic row — paired to the English values by position */}
              <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-12 md:items-end">
                <div className="md:col-span-4">
                  <FormField id={`og-name-ar-${i}`} label={`${t('form.variants.optionName', { defaultValue: 'Option name' })} · ع`}>
                    <Input id={`og-name-ar-${i}`} dir="rtl" value={g.nameAr} onChange={(e) => setOptionGroup(i, { nameAr: e.target.value })} placeholder="مثال: اللون" />
                  </FormField>
                </div>
                <div className="md:col-span-7">
                  <FormField id={`og-vals-ar-${i}`} label={`${t('form.variants.optionValues', { defaultValue: 'Values (comma-separated)' })} · ع`}>
                    <Input id={`og-vals-ar-${i}`} dir="rtl" value={g.valuesAr} onChange={(e) => setOptionGroup(i, { valuesAr: e.target.value })} placeholder="مثال: أحمر، أزرق، أخضر" />
                  </FormField>
                </div>
                {/* Fills the two Arabic boxes above from the English ones, value
                    by value, so they stay in the order the pairing depends on. */}
                {aiEnabled && (
                  <div className="flex md:col-span-1 md:justify-end">
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      loading={aiGroupBusy === i}
                      disabled={aiGroupBusy !== null || (!g.name.trim() && !g.values.trim())}
                      onClick={() => void translateOptionGroup(i)}
                      title={t('form.variants.translate', { defaultValue: 'Translate to Arabic' })}
                      aria-label={t('form.variants.translate', { defaultValue: 'Translate to Arabic' })}
                    >
                      <Languages className="h-4 w-4" aria-hidden />
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={() => setOptionGroups((gs) => [...gs, { name: '', values: '', nameAr: '', valuesAr: '' }])}>
              <Plus className="h-4 w-4" aria-hidden />
              {t('form.variants.addOption', { defaultValue: 'Add option' })}
            </Button>
            {optionGroups.length > 0 && (
              <Button type="button" size="sm" onClick={generateVariants} disabled={plannedVariantCount === 0}>
                {t('form.variants.generate', { defaultValue: 'Generate variants' })}
                {plannedVariantCount > 0 ? ` (${plannedVariantCount})` : ''}
              </Button>
            )}
          </div>
          {/* Generate keeps the price, stock and barcode of any row whose name
              still applies, and drops the rest — worth saying before it runs. */}
          {plannedVariantCount > 0 && (
            <p className="mt-2 text-xs text-fg-subtle">
              {t('form.variants.generateHint', {
                count: plannedVariantCount,
                defaultValue: 'Makes {{count}} variant(s). Rows whose name stays the same keep their price, stock and barcode.',
              })}
            </p>
          )}
        </Card>

        {/* Variants grid — each variant has its own price, stock, and barcode */}
        <Card className="p-6">
          {sectionTitle(Shapes, t('form.sections.variants', { defaultValue: 'Variants' }))}
          <p className="mb-3 text-xs text-fg-muted">
            {t('form.variants.hint', {
              defaultValue: 'Each variant is a sellable option with its own price, stock and barcode. Empty price falls back to the base price.',
            })}
          </p>
          {/* Two lines per variant rather than one row of six: the English and
              Arabic names sit side by side (their pairing is the thing people
              get wrong), and the price cell has room for the checkbox below. */}
          {(form.variants ?? []).map((v, i) => {
            const useBase = variantUsesBasePrice(v);
            return (
              <div key={i} className="mt-3 rounded-lg border border-border p-3">
                <div className="grid grid-cols-1 gap-2 md:grid-cols-12 md:items-end">
                  <div className="md:col-span-4">
                    <FormField id={`v-name-${i}`} label={`${t('form.variants.name', { defaultValue: 'Variant' })} · EN`}>
                      <Input id={`v-name-${i}`} value={v.name} onChange={(e) => setVariant(i, { name: e.target.value })} placeholder={t('form.variants.namePlaceholder', { defaultValue: 'e.g. Red / A4' })} maxLength={128} />
                    </FormField>
                  </div>
                  <div className="md:col-span-4">
                    <FormField id={`v-name-ar-${i}`} label={`${t('form.variants.name', { defaultValue: 'Variant' })} · ع`}>
                      <Input id={`v-name-ar-${i}`} dir="rtl" value={v.nameAr ?? ''} onChange={(e) => setVariant(i, { nameAr: e.target.value || undefined })} placeholder="مثال: أحمر / A4" maxLength={128} />
                    </FormField>
                  </div>
                  <div className="md:col-span-3">
                    <FormField
                      id={`v-name-en-${i}`}
                      label={t('form.variants.nameEnOverride', { defaultValue: 'English override' })}
                      helperText={t('form.variants.nameEnOverrideHint', { defaultValue: 'Only needed if Variant · EN above isn\'t actually English.' })}
                    >
                      <Input id={`v-name-en-${i}`} value={v.nameEn ?? ''} onChange={(e) => setVariant(i, { nameEn: e.target.value || undefined })} placeholder="e.g. Red" maxLength={128} />
                    </FormField>
                  </div>
                  <div className="flex md:col-span-1 md:justify-end">
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeVariant(i)} aria-label={tc('actions.remove', { defaultValue: 'Remove' })} title={tc('actions.remove', { defaultValue: 'Remove' })}>
                      <X className="h-4 w-4" aria-hidden />
                    </Button>
                  </div>
                </div>
                <div className="mt-2 grid grid-cols-1 gap-2 md:grid-cols-12 md:items-start">
                  <div className="md:col-span-4">
                    <FormField id={`v-price-${i}`} label={t('create.salePrice')}>
                      {useBase ? (
                        <p className="flex h-10 items-center text-sm text-fg-muted">
                          {(form.salePrice ?? '').trim()
                            ? formatMoney(form.salePrice ?? '0', { locale: i18n.language, currency: form.saleCurrencyCode ?? baseCurrency })
                            : t('form.variants.basePriceUnset', { defaultValue: 'the product price' })}
                        </p>
                      ) : (
                        <NumberInput id={`v-price-${i}`} value={v.salePrice ?? ''} onValueChange={(raw) => setVariant(i, { salePrice: raw || undefined })} placeholder="0.00" />
                      )}
                    </FormField>
                    <label className="mt-1 inline-flex items-center gap-2 text-xs text-fg-muted">
                      <input type="checkbox" checked={useBase} onChange={(e) => setVariantPriceMode(i, e.target.checked)} />
                      <span>{t('form.variants.samePrice', { defaultValue: 'Same price as the product' })}</span>
                    </label>
                  </div>
                  <div className="md:col-span-3">
                    <FormField id={`v-stock-${i}`} label={t('form.variants.stock', { defaultValue: 'Stock' })}>
                      <NumberInput id={`v-stock-${i}`} value={v.stockQty ?? ''} onValueChange={(raw) => setVariant(i, { stockQty: raw || undefined })} placeholder="—" />
                    </FormField>
                  </div>
                  <div className="md:col-span-5">
                    <FormField id={`v-bc-${i}`} label={t('create.barcode')}>
                      <div className="flex gap-1.5">
                        <Input id={`v-bc-${i}`} value={v.barcode ?? ''} onChange={(e) => setVariant(i, { barcode: e.target.value || undefined })} maxLength={64} className="flex-1" />
                        <Button
                          type="button"
                          variant="secondary"
                          className="flex-none"
                          onClick={() => setVariant(i, { barcode: generateBarcodeValue(form.barcodeFormat ?? 'code128') })}
                          title={t('create.generateBarcode', { defaultValue: 'Generate barcode' })}
                          aria-label={t('create.generateBarcode', { defaultValue: 'Generate barcode' })}
                        >
                          <Barcode className="h-4 w-4" aria-hidden />
                        </Button>
                      </div>
                    </FormField>
                  </div>
                </div>
              </div>
            );
          })}
          <div className="mt-3 flex flex-wrap gap-2">
            <Button type="button" variant="secondary" size="sm" onClick={addVariant}>
              <Plus className="h-4 w-4" aria-hidden />
              {t('form.variants.add', { defaultValue: 'Add variant' })}
            </Button>
            {/* For rows typed by hand — generated ones are already bilingual. */}
            {aiEnabled && (form.variants ?? []).length > 0 && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={aiNamesBusy}
                disabled={aiNamesBusy}
                onClick={() => void translateVariantNames()}
              >
                <Languages className="h-4 w-4" aria-hidden />
                {t('form.variants.translateNames', { defaultValue: 'Translate names' })}
              </Button>
            )}
            {/* The mirror action, for a catalog typed in Arabic (or any non-English
                working language) whose English shoppers were seeing that label. */}
            {aiEnabled && (form.variants ?? []).length > 0 && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                loading={aiNamesEnBusy}
                disabled={aiNamesEnBusy}
                onClick={() => void translateVariantNamesEn()}
              >
                <Languages className="h-4 w-4" aria-hidden />
                {t('form.variants.translateNamesEn', { defaultValue: 'Translate to English' })}
              </Button>
            )}
          </div>
        </Card>
            <DialogFooter>
              <Button type="button" onClick={() => setVariantsOpen(false)}>{t('form.variants.done', { defaultValue: 'Done' })}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

          </TabsContent>

          <TabsContent value="storefront" className="mt-4 flex flex-col gap-4">
        {/* Storefront / app */}
        <Card className="p-6">
          {sectionTitle(MonitorPlay, t('form.sections.storefront', { defaultValue: 'Storefront / app' }))}
          <FormField
            id="it-video"
            label={fieldLabel(Video, t('create.videoUrl', { defaultValue: 'Video link (YouTube)' }))}
            helperText={t('create.videoUrlHint', { defaultValue: 'Paste a YouTube link to show a product video in the app.' })}
          >
            <Input
              id="it-video"
              type="url"
              value={form.videoUrl ?? ''}
              onChange={(e) => setField({ videoUrl: e.target.value })}
              placeholder="https://youtu.be/…"
              maxLength={500}
            />
            {/* Say so here rather than let the shop find out by a video that never
                appears: the store and the app show this only when it parses. */}
            {(form.videoUrl ?? '').trim() !== '' && !isYouTubeUrl(form.videoUrl) && (
              <p className="mt-1 text-xs text-warning">
                {t('create.videoUrlInvalid', { defaultValue: 'Not a YouTube link — no video will show. Use a youtube.com or youtu.be link.' })}
              </p>
            )}
          </FormField>
          <div className="mt-3 flex flex-col gap-2">
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.isPublished ?? false} onChange={(e) => setField({ isPublished: e.target.checked })} />
              <span>{t('create.isPublished', { defaultValue: 'Publish to the store and app' })}</span>
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.isHidden ?? false} onChange={(e) => setField({ isHidden: e.target.checked })} />
              <span>{t('create.isHidden', { defaultValue: 'Hide in the app' })}</span>
            </label>
            {manufacturingEnabled && (
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={form.isRawMaterial ?? false} onChange={(e) => setField({ isRawMaterial: e.target.checked })} />
                <span>{t('create.isRawMaterial', { defaultValue: 'Raw material (informational — stays sellable and purchasable)' })}</span>
              </label>
            )}
            <label className="inline-flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.isFeatured ?? false} onChange={(e) => setField({ isFeatured: e.target.checked })} />
              <span>{t('create.isFeatured', { defaultValue: 'Featured' })}</span>
            </label>
          </div>
        </Card>

          </TabsContent>

          {canReadStock && (
            <TabsContent value="stock" className="mt-4 flex flex-col gap-4">
              <ItemStockTab
                itemId={edit && initial ? initial.id : null}
                item={{ code: form.code, name: form.name }}
                opening={opening}
                onOpeningChange={setOpening}
              />
            </TabsContent>
          )}
          {manufacturingEnabled && canReadMfg && edit && initial && form.itemType === 'product' && (
            <TabsContent value="recipe" className="mt-4 flex flex-col gap-4">
              <ItemRecipeTab itemId={initial.id} itemUom={form.unitOfMeasure} canWrite={canWriteMfg} />
            </TabsContent>
          )}
          {combosEnabled && form.itemType === 'product' && (
            <TabsContent value="combo" className="mt-4 flex flex-col gap-4">
              {/* Price comes from the live form, so the savings figure moves as
                  the owner types — that is the number a bundle is built around. */}
              <ItemComboTab
                itemId={edit && initial ? initial.id : null}
                salePrice={form.salePrice ?? ''}
                saleCurrencyCode={form.saleCurrencyCode ?? ''}
                canWrite={canWriteItems}
                draft={comboDraft}
                onDraftChange={setComboDraft}
              />
            </TabsContent>
          )}
        </Tabs>

        {error && <p className="text-sm text-danger" role="alert">{error}</p>}

        <div className="sticky bottom-0 flex gap-2 border-t border-border bg-bg/90 py-3 backdrop-blur">
          <Button type="submit" loading={saving}>
            {edit ? t('edit.submit') : t('create.submit')}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate(edit && initial ? `/inventory/items/${initial.id}` : '/inventory/items')}
            disabled={saving}
          >
            {tc('actions.cancel')}
          </Button>
        </div>
      </form>

      <ImageEditorDialog
        open={editorOpen}
        source={editorFile}
        title={t('create.editImage', { defaultValue: 'Edit item image' })}
        onCancel={() => {
          setEditorOpen(false);
          setEditorFile(null);
        }}
        onSave={onEditedImage}
      />

      {imagegenEnabled && (
        <AiImageDialog
          open={aiImgOpen}
          onClose={() => setAiImgOpen(false)}
          name={form.name ?? ''}
          brand={(() => { const b = brandCombo.find((o) => o.value === form.brandId); return b && b.value !== NONE_OPT ? b.label : undefined; })()}
          category={(() => { const c = categoryCombo.find((o) => o.value === form.categoryId); return c && c.value !== NONE_OPT ? c.label : undefined; })()}
          onUse={onEditedImage}
        />
      )}
    </div>
  );
}

/** One dynamic input on the item form, rendered to match its field type. */
function CustomFieldInput({
  def,
  value,
  onChange,
}: {
  def: CustomFieldDefinition;
  value: unknown;
  onChange: (v: unknown) => void;
}): JSX.Element {
  const { t } = useTranslation('items');
  const id = `cf-${def.code}`;
  const selectClass = 'block w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-fg';

  if (def.fieldType === 'boolean') {
    return (
      <label className="inline-flex items-center gap-2 self-end text-sm">
        <input type="checkbox" checked={Boolean(value)} onChange={(e) => onChange(e.target.checked)} />
        <span>
          {def.label}
          {def.isRequired ? ' *' : ''}
        </span>
      </label>
    );
  }
  if (def.fieldType === 'longtext') {
    return (
      <FormField id={id} label={def.label} required={def.isRequired}>
        <textarea id={id} rows={3} className={selectClass} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)} />
      </FormField>
    );
  }
  if (def.fieldType === 'select') {
    return (
      <FormField id={id} label={def.label} required={def.isRequired}>
        <select id={id} className={selectClass} value={String(value ?? '')} onChange={(e) => onChange(e.target.value)}>
          <option value="">{t('create.categoryNone', { defaultValue: '— None —' })}</option>
          {(def.options ?? []).map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
      </FormField>
    );
  }
  if (def.fieldType === 'multiselect') {
    const arr = Array.isArray(value) ? (value as string[]) : [];
    return (
      <FormField id={id} label={def.label} required={def.isRequired}>
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 pt-1.5">
          {(def.options ?? []).map((o) => (
            <label key={o} className="inline-flex items-center gap-1.5 text-sm">
              <input
                type="checkbox"
                checked={arr.includes(o)}
                onChange={() => {
                  const next = arr.includes(o) ? arr.filter((x) => x !== o) : [...arr, o];
                  onChange(next);
                }}
              />
              <span>{o}</span>
            </label>
          ))}
        </div>
      </FormField>
    );
  }
  return (
    <FormField id={id} label={def.label} required={def.isRequired}>
      <Input
        id={id}
        type={def.fieldType === 'date' ? 'date' : 'text'}
        inputMode={def.fieldType === 'number' ? 'decimal' : undefined}
        value={value === undefined || value === null ? '' : String(value)}
        onChange={(e) => onChange(e.target.value)}
      />
    </FormField>
  );
}
